import {
  validateDocumentTemplateFamilyAdmission,
} from "./document-template-family-admission.ts";
import {
  type CogoDocumentTemplateFamily,
  type CogoDocumentTemplateFamilyVariant,
} from "./document-template-family-runtime.ts";
import {
  canonicalizeDocumentTemplateJson,
  resolveDocumentTemplatePointer,
  sha256CanonicalJson,
  sha256DocumentTemplateText,
  type CogoDocumentTemplateManifest,
  validateDocumentTemplateManifest,
  validateDocumentTemplateInput,
} from "./document-template-runtime.ts";

export const COGO_DOCUMENT_GENERATION_IR_V2_SCHEMA_VERSION =
  "cogo.document-generation-ir.v2" as const;

export const COGO_DOCUMENT_TEMPLATE_FLOW_PAGE_SCHEMA_VERSION =
  "cogo.document-template-flow-page.v1" as const;

export const COGO_FLOW_PAGE_PDF_VALUE_HASH_ALGORITHM =
  "cogo.pdf-semantic-value-sha256.v1" as const;

export const COGO_FLOW_PAGE_PDF_SEMANTIC_VALUE_CONTRACT = {
  textNormalization: "unicode-nfc-lf-collapse-horizontal-space-v1",
  richTextBasis: "normalized-string",
  staticTextBasis: "normalized-string",
  headingBasis: "normalized-string",
  tableBasis: "normalized-row-major-string-matrix-with-header-row",
  canonicalization: "cogo.canonical-json.v1",
  digest: "sha256",
} as const;

export const COGO_DOCUMENT_GENERATION_IR_V2_COMPILER = {
  id: "cogo.document-template-profile-ir-compiler",
  version: "2.0.0",
  canonicalization: "cogo.canonical-json.v1",
  deterministic: true,
  finalWriter: false,
  proofAuthority: false,
  promotionAuthority: false,
} as const;

export type CogoFlowPageNativeTarget = "docx" | "hwpx" | "pdf";

/**
 * The FlowPage static-content vocabulary, in canonical write-plan order.
 *
 * A layout does not have to carry all of it. Membership splits into two
 * groups below, derived from what the generated writers unconditionally
 * render versus what they render only for a layout that declares the
 * section (scripts/agentic/document_template_generated_writer.py):
 *
 *   - eyebrow/title/subtitle/footer are written on every DOCX and HWPX
 *     (`_write_docx` masthead + title + subtitle + section footer);
 *   - decisionLabel is the shaded decision banner, written only where the
 *     layout declares the text (and it is the only reader of
 *     resolvedStyleSheet.colors.accentSoft);
 *   - evaluationNote is the trailing note block (the second reader of
 *     resolvedStyleSheet.typeScalePt.tableBody).
 */
export const COGO_FLOW_PAGE_STATIC_CONTENT_KEYS = [
  "eyebrow",
  "title",
  "subtitle",
  "decisionLabel",
  "evaluationNote",
  "footer",
] as const;

/** Static text every FlowPage writer renders unconditionally. */
export const COGO_FLOW_PAGE_BASE_STATIC_CONTENT_KEYS = [
  "eyebrow",
  "title",
  "subtitle",
  "footer",
] as const;

/**
 * Static text that declares an optional document section. Present ⇒ the
 * section is rendered and its style vocabulary is required; absent ⇒ the
 * section is not rendered and the style keys only it could consume are
 * rejected rather than carried as dead weight.
 */
export const COGO_FLOW_PAGE_SECTION_STATIC_CONTENT_KEYS = [
  "decisionLabel",
  "evaluationNote",
] as const;

export type CogoFlowPageStaticContentKey =
  (typeof COGO_FLOW_PAGE_STATIC_CONTENT_KEYS)[number];

export type CogoFlowPageBaseStaticContentKey =
  (typeof COGO_FLOW_PAGE_BASE_STATIC_CONTENT_KEYS)[number];

/**
 * Ordered type-scale steps the masthead eyebrow may resolve to, most
 * specific first. `_write_docx` sizes the eyebrow from the declared
 * `tableHeader` step (the reference family's 8.4pt); a layout that declares
 * no table declares no `tableHeader` step, and the eyebrow resolves to the
 * next declared step in this list. `footer` is a base-required step, so the
 * list always resolves. This is why `tableHeader` stays an allowed key for a
 * tableless layout instead of being rejected: whenever it is declared, the
 * eyebrow reads it.
 */
export const COGO_FLOW_PAGE_EYEBROW_TYPE_SCALE_STEPS = [
  "tableHeader",
  "footer",
] as const;

/**
 * What a FlowPage layout declares about its own document structure. Every
 * conditional requirement in this module — and the matching conditional
 * render in the Python writer — is derived from this, never from the
 * reference family's section list.
 */
export interface CogoFlowPageDeclaredSections {
  /** Declared static keys, in canonical vocabulary order. */
  staticContentKeys: CogoFlowPageStaticContentKey[];
  /** `staticContent.decisionLabel` — the accentSoft-shaded banner. */
  decisionBanner: boolean;
  /** `staticContent.evaluationNote` — the trailing soft-shaded note. */
  evaluationNote: boolean;
  /** At least one `slotBindings[].slotKind === "table"`. */
  table: boolean;
  /** `assetPlacements[].placement === "masthead-start"` count. */
  mastheadAssets: number;
}

/**
 * Reads a layout's declared sections. Deliberately tolerant of a
 * not-yet-validated value: it is called from validateDocumentTemplateFlowPage
 * before the shape checks run, so an invalid layout must still yield a
 * derivation (the shape errors are reported separately).
 */
export function declaredCogoFlowPageSections(
  layout: unknown,
): CogoFlowPageDeclaredSections {
  const record = isRecord(layout) ? layout : {};
  const staticContent = isRecord(record.staticContent)
    ? record.staticContent
    : {};
  const staticContentKeys = COGO_FLOW_PAGE_STATIC_CONTENT_KEYS.filter(
    (key) => key in staticContent,
  );
  const slotBindings = Array.isArray(record.slotBindings)
    ? record.slotBindings
    : [];
  const assetPlacements = Array.isArray(record.assetPlacements)
    ? record.assetPlacements
    : [];
  return {
    staticContentKeys: [...staticContentKeys],
    decisionBanner: staticContentKeys.includes("decisionLabel"),
    evaluationNote: staticContentKeys.includes("evaluationNote"),
    table: slotBindings.some(
      (binding) => isRecord(binding) && binding.slotKind === "table",
    ),
    mastheadAssets: assetPlacements.filter(
      (placement) =>
        isRecord(placement) && placement.placement === "masthead-start",
    ).length,
  };
}

const COGO_FLOW_PAGE_TARGET_ORDER: readonly CogoFlowPageNativeTarget[] = [
  "docx",
  "hwpx",
  "pdf",
];

export interface CogoDocumentJsonArtifactInput {
  ref: string;
  sourceText: string;
  value: unknown;
}

export interface CogoDocumentArtifactInput {
  ref: string;
  bytes: Uint8Array;
  mediaType: string;
  role: "semantic-source" | "template-source" | "native-template" | "asset";
  target?: CogoFlowPageNativeTarget;
}

export interface CogoDocumentSemanticEvidenceInput {
  evidenceRef: string;
  sourceRef: string;
  fragmentText: string;
}

export interface CogoDocumentHashedArtifactRef {
  ref: string;
  sha256: string;
  mediaType: string;
  role: "semantic-source" | "template-source" | "native-template" | "asset";
  target?: CogoFlowPageNativeTarget;
}

export interface CogoFlowPageTableColumn {
  key: string;
  label: string;
  widthRatio: number;
}

export type CogoFlowPageDocxAnchor = {
  kind: "bookmark";
  part: "word/document.xml" | "word/footer1.xml";
  value: string;
  expectedOccurrence: 1;
};

export type CogoFlowPageHwpxAnchor =
  | {
      kind: "paragraph-id";
      part: "Contents/section0.xml";
      value: string;
      expectedOccurrence: 1;
    }
  | {
      kind: "table-id";
      part: "Contents/section0.xml";
      value: string;
      expectedOccurrence: 1;
    };

/**
 * A PDF-native semantic/render locator. The writer materializes `value` in the
 * PDF NameTree /Dests and binds that destination to the page carrying the
 * corresponding tagged structure role. Flow pagination is resolved by the
 * writer, so the IR deliberately does not predict a page index.
 */
export type CogoFlowPagePdfAnchor = {
  kind: "named-destination";
  structureType: "H1" | "H2" | "P" | "Table";
  value: string;
  expectedOccurrence: 1;
  pageAssociationRequired: true;
};

export type CogoFlowPageDocxAssetAnchor = {
  kind: "package-relationship";
  part: "word/document.xml";
  relationshipsPart: "word/_rels/document.xml.rels";
  relationshipId: string;
  relationshipType: "http://schemas.openxmlformats.org/officeDocument/2006/relationships/image";
  packagePart: string;
  expectedOccurrence: 1;
  contentType: {
    kind: "extension-content-type";
    part: "[Content_Types].xml";
    extension: "svg";
    mediaType: "image/svg+xml";
    expectedOccurrence: 1;
  };
  renderNode: {
    kind: "svg-blip";
    part: "word/document.xml";
    relationshipId: string;
    expectedOccurrence: 1;
    extentEmu: {
      width: number;
      height: number;
    };
    container: {
      kind: "table-cell";
      tableIndex: 0;
      rowIndex: 0;
      cellIndex: 0;
    };
  };
};

export type CogoFlowPageHwpxAssetAnchor = {
  kind: "package-manifest-item";
  part: "Contents/content.hpf";
  manifestItemId: string;
  packagePart: string;
  mediaType: string;
  expectedOccurrence: 1;
  renderNode: {
    kind: "image-binary-ref";
    part: "Contents/section0.xml";
    binaryItemId: string;
    expectedOccurrence: 1;
    extentHwp: {
      width: number;
      height: number;
    };
    container: {
      kind: "paragraph-id";
      value: "14";
      relation: "before";
      relativeToParagraphId: "10";
    };
  };
};

export type CogoFlowPageDocxFooterLink = {
  kind: "footer-reference";
  documentPart: "word/document.xml";
  relationshipsPart: "word/_rels/document.xml.rels";
  relationshipId: "rIdCogoFooter1";
  relationshipType: "http://schemas.openxmlformats.org/officeDocument/2006/relationships/footer";
  targetPart: "word/footer1.xml";
  expectedOccurrence: 1;
  renderNode: {
    kind: "section-footer-reference";
    part: "word/document.xml";
    referenceType: "default";
    relationshipId: "rIdCogoFooter1";
    expectedOccurrence: 1;
  };
};

interface CogoFlowPageSlotBindingBase {
  slotId: string;
  nodeId: string;
  semanticRole: string;
  inputBindingPath: string;
  heading: string;
  styleTokenRefs: string[];
  targetAnchors: {
    docx: CogoFlowPageDocxAnchor;
    hwpx: CogoFlowPageHwpxAnchor;
  };
}

export type CogoFlowPageSlotBinding =
  | (CogoFlowPageSlotBindingBase & {
      slotKind: "rich-text";
    })
  | (CogoFlowPageSlotBindingBase & {
      slotKind: "table";
      columns: CogoFlowPageTableColumn[];
    });

export interface CogoDocumentTemplateFlowPage {
  schemaVersion: typeof COGO_DOCUMENT_TEMPLATE_FLOW_PAGE_SCHEMA_VERSION;
  layoutId: string;
  familyId: string;
  variantId: string;
  profile: "flow-page";
  // A family-owned free-form label (e.g. "standard_business_brief",
  // "standard_report_five_section") — not a compiler-enforced enum. See the
  // designPreset validation comment in validateDocumentTemplateFlowPage.
  designPreset: string;
  page: {
    size: "letter" | "a4";
    flow: true;
    marginInches: {
      top: number;
      right: number;
      bottom: number;
      left: number;
    };
  };
  // Base keys are always written; decisionLabel/evaluationNote declare the
  // optional banner and note sections (see
  // COGO_FLOW_PAGE_SECTION_STATIC_CONTENT_KEYS).
  staticContent: {
    eyebrow: string;
    title: string;
    subtitle: string;
    decisionLabel?: string;
    evaluationNote?: string;
    footer: string;
  };
  styleTokenRefs: string[];
  resolvedStyleSheet: {
    fontFamilies: {
      latin: string;
      eastAsia: string;
    };
    colors: {
      navy: string;
      slate: string;
      muted: string;
      line: string;
      soft: string;
      // Never read downstream (verified by grep, TS + Python writer, both
      // docx and hwpx paths) — optional so families that omit it (e.g.
      // standard-report, reb-report) still admit, while golden's declared
      // value stays allowed and shape-validated.
      accent?: string;
      // Read only by the decision banner (_write_docx `_set_cell_shading`),
      // so it is required with a declared decisionLabel and rejected without.
      accentSoft?: string;
      white: string;
    };
    typeScalePt: {
      title: number;
      subtitle: number;
      sectionHeading: number;
      body: number;
      // Read by the masthead eyebrow (always, when declared), the decision
      // banner, and table header cells.
      tableHeader?: number;
      // Read by table body cells and the evaluation note only.
      tableBody?: number;
      footer: number;
    };
    spacingPt: {
      bodyAfter: number;
      sectionBefore: number;
      sectionAfter: number;
    };
    // Table geometry: required by a layout that declares a table slot,
    // rejected for one that does not.
    table?: {
      widthDxa: number;
      indentDxa: number;
      cellMarginsDxa: {
        top: number;
        right: number;
        bottom: number;
        left: number;
      };
    };
  };
  assetPlacements: Array<{
    assetId: string;
    placement: "masthead-start";
    widthInches: number;
    heightInches: number;
  }>;
  slotBindings: CogoFlowPageSlotBinding[];
  unsupportedNativePartPolicy: "preserve-or-block";
}

export interface CogoDocumentGenerationIrV2JsonLineage {
  ref: string;
  byteSha256: string;
  canonicalSha256: string;
}

interface CogoDocumentGenerationIrV2SlotOperationBase {
  operationId: string;
  bindingKind: "slot";
  bindingId: string;
  operation: "create-rich-text" | "create-table";
  slotId: string;
  nodeId: string;
  semanticRole: string;
  valueRef: string;
  valueHash: string;
  valueHashAlgorithm?: typeof COGO_FLOW_PAGE_PDF_VALUE_HASH_ALGORITHM;
  anchor:
    | CogoFlowPageDocxAnchor
    | CogoFlowPageHwpxAnchor
    | CogoFlowPagePdfAnchor;
  heading: {
    value: string;
    valueHash: string;
    valueHashAlgorithm?: typeof COGO_FLOW_PAGE_PDF_VALUE_HASH_ALGORITHM;
    anchor:
      | CogoFlowPageDocxAnchor
      | CogoFlowPageHwpxAnchor
      | CogoFlowPagePdfAnchor;
  };
}

export interface CogoDocumentGenerationIrV2TableStructure {
  kind: "table";
  /** Exact raw authoring metadata; PDF semantic normalization is hash-only. */
  columns: CogoFlowPageTableColumn[];
  /** Canonical hash of the raw columns above, not their normalized labels. */
  columnsHash: string;
  widthDxa: number;
  indentDxa: number;
  rowCount: number;
  /** PDF-only rendering geometry. Native package operations must omit it. */
  cellMarginsDxa?: never;
}

export interface CogoDocumentGenerationIrV2PdfTableStructure
  extends Omit<CogoDocumentGenerationIrV2TableStructure, "cellMarginsDxa"> {
  cellMarginsDxa: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };
}

export type CogoDocumentGenerationIrV2SlotOperation =
  CogoDocumentGenerationIrV2SlotOperationBase &
    (
      | {
          target: "pdf";
          structure:
            | { kind: "rich-text" }
            | CogoDocumentGenerationIrV2PdfTableStructure;
        }
      | {
          target: Exclude<CogoFlowPageNativeTarget, "pdf">;
          structure:
            | { kind: "rich-text" }
            | CogoDocumentGenerationIrV2TableStructure;
        }
    );

export interface CogoDocumentGenerationIrV2StaticOperation {
  operationId: string;
  target: CogoFlowPageNativeTarget;
  bindingKind: "static";
  bindingId: CogoFlowPageStaticContentKey;
  operation: "create-static-text";
  nodeId: string;
  semanticRole: string;
  valueRef: string;
  valueHash: string;
  valueHashAlgorithm?: typeof COGO_FLOW_PAGE_PDF_VALUE_HASH_ALGORITHM;
  anchor:
    | CogoFlowPageDocxAnchor
    | CogoFlowPageHwpxAnchor
    | CogoFlowPagePdfAnchor;
  documentLink?: CogoFlowPageDocxFooterLink;
}

export interface CogoDocumentGenerationIrV2AssetOperation {
  operationId: string;
  target: Exclude<CogoFlowPageNativeTarget, "pdf">;
  bindingKind: "asset";
  bindingId: string;
  operation: "embed-asset";
  assetId: string;
  nodeId: string;
  semanticRole: string;
  valueRef: string;
  valueHash: string;
  anchor: CogoFlowPageDocxAssetAnchor | CogoFlowPageHwpxAssetAnchor;
  placement: CogoDocumentTemplateFlowPage["assetPlacements"][number];
  transform: {
    id: "cogo.asset-embed.identity";
    version: "1.0.0";
    mode: "identity";
    sourceMediaType: string;
    outputMediaType: string;
    sourceSha256: string;
    outputSha256: string;
  };
}

export type CogoDocumentGenerationIrV2Operation =
  | CogoDocumentGenerationIrV2SlotOperation
  | CogoDocumentGenerationIrV2StaticOperation
  | CogoDocumentGenerationIrV2AssetOperation;

export interface CogoDocumentGenerationIrV2SemanticProvenance {
  coverage: "evidence-ref-fields-only";
  groundingMode: "declared-exact-utf8-fragment";
  semanticEntailmentVerified: false;
  evidence: Array<{
    evidenceRef: string;
    sourceRef: string;
    sourceSha256: string;
    fragment: {
      kind: "utf8-byte-range";
      start: number;
      end: number;
      byteSha256: string;
      expectedOccurrence: 1;
    };
  }>;
}

export interface CogoDocumentGenerationIrV2ResolvedAsset {
  assetId: string;
  role: string;
  artifact: CogoDocumentHashedArtifactRef & { role: "asset" };
  placement: CogoDocumentTemplateFlowPage["assetPlacements"][number];
}

export interface CogoDocumentGenerationIrV2TargetPlan {
  target: CogoFlowPageNativeTarget;
  adapter: string;
  sourceMode: "generated-from-cogo" | "native-template";
  nativeTemplateArtifact?: CogoDocumentHashedArtifactRef;
  writer: {
    id: string;
    version: string;
    deterministic: true;
    consumesExactPlan: true;
  };
  reader: {
    id: string;
    version: string;
    anchorLocal: true;
    recomputesFromArtifact: true;
  };
  contentType: string;
  requiredPackageParts: string[];
  profileLayoutHash: string;
  resolvedAssetsHash: string;
  operations: CogoDocumentGenerationIrV2Operation[];
  readback: {
    semanticValueHashesRequired: true;
    nativeAnchorsRequired: true;
    externalConsumerOpenabilityRequired: true;
  } & (
    | { packageStructureRequired: true }
    | { documentStructureRequired: true }
  );
  planHash: string;
}

export interface CogoDocumentGenerationIrV2 {
  schemaVersion: typeof COGO_DOCUMENT_GENERATION_IR_V2_SCHEMA_VERSION;
  generationId: string;
  createdAt: string;
  profile: "flow-page";
  compiler: typeof COGO_DOCUMENT_GENERATION_IR_V2_COMPILER;
  family: CogoDocumentGenerationIrV2JsonLineage & {
    familyId: string;
    familyVersion: string;
  };
  variant: {
    variantId: string;
    profile: "flow-page";
    canonicalSha256: string;
  };
  layout: CogoDocumentGenerationIrV2JsonLineage & {
    layoutId: string;
  };
  inputSchema: CogoDocumentGenerationIrV2JsonLineage;
  semanticInput: CogoDocumentGenerationIrV2JsonLineage;
  semanticProvenance: CogoDocumentGenerationIrV2SemanticProvenance;
  sourceArtifacts: CogoDocumentHashedArtifactRef[];
  profileLayout: CogoDocumentTemplateFlowPage;
  resolvedAssets: CogoDocumentGenerationIrV2ResolvedAsset[];
  bindings: Record<string, unknown>;
  writePlan: CogoDocumentGenerationIrV2Operation[];
  requestedTargets: CogoFlowPageNativeTarget[];
  targetPlans: CogoDocumentGenerationIrV2TargetPlan[];
  unsupportedNativePartPolicy: "preserve-or-block";
  structureHash: string;
  contentHash: string;
  proofBindingHash: string;
}

export interface CompileCogoFlowPageGenerationIrV2Input {
  family: CogoDocumentJsonArtifactInput;
  variantId: string;
  layout: CogoDocumentJsonArtifactInput;
  inputSchema: CogoDocumentJsonArtifactInput;
  semanticInput: CogoDocumentJsonArtifactInput;
  semanticEvidence: CogoDocumentSemanticEvidenceInput[];
  sourceArtifacts: CogoDocumentArtifactInput[];
  nativeTemplateArtifacts?: Partial<
    Record<CogoFlowPageNativeTarget, CogoDocumentArtifactInput>
  >;
  requestedTargets: CogoFlowPageNativeTarget[];
  createdAt: string;
}

export interface VerifyCogoFlowPageGenerationIrV2Resources {
  family: CogoDocumentJsonArtifactInput;
  layout: CogoDocumentJsonArtifactInput;
  inputSchema: CogoDocumentJsonArtifactInput;
  semanticInput: CogoDocumentJsonArtifactInput;
  semanticEvidence: CogoDocumentSemanticEvidenceInput[];
  sourceArtifacts: CogoDocumentArtifactInput[];
  request: {
    variantId: string;
    requestedTargets: CogoFlowPageNativeTarget[];
    createdAt: string;
  };
  nativeTemplateArtifacts?: Partial<
    Record<CogoFlowPageNativeTarget, CogoDocumentArtifactInput>
  >;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function isSha256(value: unknown): value is string {
  return typeof value === "string" && /^sha256:[a-f0-9]{64}$/.test(value);
}

function isFlowPageIdentifier(value: unknown): value is string {
  return (
    typeof value === "string" &&
    /^[a-z0-9]+(?:[.-][a-z0-9]+)*$/.test(value)
  );
}

function isFlowPageSlotIdentifier(value: unknown): value is string {
  return isFlowPageIdentifier(value) && value.length <= 27;
}

function isFlowPageBindingKey(value: unknown): value is string {
  return (
    typeof value === "string" &&
    /^[A-Za-z_][A-Za-z0-9._-]*$/.test(value)
  );
}

async function sha256Bytes(value: Uint8Array): Promise<string> {
  const bytes = new Uint8Array(value.byteLength);
  bytes.set(value);
  const digest = await globalThis.crypto.subtle.digest("SHA-256", bytes);
  return `sha256:${Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("")}`;
}

function unique(values: readonly string[]): boolean {
  return new Set(values).size === values.length;
}

function validateOnlyKeys(
  value: Record<string, unknown>,
  allowed: readonly string[],
  path: string,
  errors: string[],
): void {
  const allowedSet = new Set(allowed);
  for (const key of Object.keys(value)) {
    if (!allowedSet.has(key)) errors.push(`${path}.${key} is not allowed.`);
  }
}

function resolveDeclaredRef(baseRef: string, declaredRef: string): string {
  const hashIndex = declaredRef.indexOf("#");
  const pathRef = hashIndex >= 0 ? declaredRef.slice(0, hashIndex) : declaredRef;
  const fragment = hashIndex >= 0 ? declaredRef.slice(hashIndex) : "";
  if (/^[A-Za-z][A-Za-z0-9+.-]*:/.test(pathRef) || pathRef.startsWith("/")) {
    return `${pathRef}${fragment}`;
  }
  const basePath = baseRef.split("#", 1)[0];
  if (/^[A-Za-z][A-Za-z0-9+.-]*:\/\//.test(basePath)) {
    try {
      return `${new URL(pathRef, basePath).toString()}${fragment}`;
    } catch {
      throw new Error(
        `Cannot resolve relative resource ${declaredRef} against ${baseRef}.`,
      );
    }
  }
  const absolutePath = basePath.startsWith("/");
  const segments = [...basePath.split("/").slice(0, -1), ...pathRef.split("/")];
  const normalized: string[] = [];
  for (const segment of segments) {
    if (!segment || segment === ".") continue;
    if (segment === "..") {
      normalized.pop();
      continue;
    }
    normalized.push(segment);
  }
  return `${absolutePath ? "/" : ""}${normalized.join("/")}${fragment}`;
}

function resolveResourcePointer(value: unknown, pointer: string): unknown {
  if (!pointer.startsWith("/")) {
    throw new Error(`Resource binding must be a JSON pointer: ${pointer}`);
  }
  let current = value;
  for (const rawPart of pointer.slice(1).split("/")) {
    const part = rawPart.replaceAll("~1", "/").replaceAll("~0", "~");
    if (Array.isArray(current)) {
      if (!/^(?:0|[1-9][0-9]*)$/.test(part)) {
        throw new Error(`Resource array pointer is invalid: ${pointer}`);
      }
      const index = Number(part);
      if (index >= current.length) {
        throw new Error(`Resource binding does not resolve: ${pointer}`);
      }
      current = current[index];
      continue;
    }
    if (!isRecord(current) || !(part in current)) {
      throw new Error(`Resource binding does not resolve: ${pointer}`);
    }
    current = current[part];
  }
  return current;
}

function assertValidDateTime(value: string): void {
  const match =
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d+)?(?:Z|[+-](\d{2}):(\d{2}))$/.exec(
      value,
    );
  if (!match || Number.isNaN(Date.parse(value))) {
    throw new Error("createdAt must be an ISO date-time.");
  }
  const [, yearText, monthText, dayText, hourText, minuteText, secondText] =
    match;
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const hour = Number(hourText);
  const minute = Number(minuteText);
  const second = Number(secondText);
  const offsetHour = match[7] === undefined ? 0 : Number(match[7]);
  const offsetMinute = match[8] === undefined ? 0 : Number(match[8]);
  const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const daysInMonth = [
    31,
    leapYear ? 29 : 28,
    31,
    30,
    31,
    30,
    31,
    31,
    30,
    31,
    30,
    31,
  ];
  if (
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > daysInMonth[month - 1] ||
    hour > 23 ||
    minute > 59 ||
    second > 59 ||
    offsetHour > 23 ||
    offsetMinute > 59
  ) {
    throw new Error("createdAt must be an ISO date-time.");
  }
}

function validateArtifactRef(
  value: CogoDocumentHashedArtifactRef,
  path: string,
): string[] {
  const errors: string[] = [];
  if (!isNonEmptyString(value.ref)) errors.push(`${path}.ref is required.`);
  if (!isSha256(value.sha256)) errors.push(`${path}.sha256 must be SHA-256.`);
  if (!isNonEmptyString(value.mediaType)) {
    errors.push(`${path}.mediaType is required.`);
  }
  if (!(["semantic-source", "template-source", "native-template", "asset"] as const).includes(value.role)) {
    errors.push(`${path}.role is unsupported.`);
  }
  if (
    value.target !== undefined &&
    !(["docx", "hwpx", "pdf"] as const).includes(value.target)
  ) {
    errors.push(`${path}.target is unsupported.`);
  }
  if (value.role === "native-template" && value.target === undefined) {
    errors.push(`${path}.target is required for a native template.`);
  }
  if (value.role !== "native-template" && value.target !== undefined) {
    errors.push(`${path}.target is only allowed for native templates.`);
  }
  return errors;
}

function validateArtifactInput(
  value: CogoDocumentArtifactInput,
  path: string,
): string[] {
  const errors: string[] = [];
  if (!isNonEmptyString(value.ref)) errors.push(`${path}.ref is required.`);
  if (!(value.bytes instanceof Uint8Array) || value.bytes.byteLength === 0) {
    errors.push(`${path}.bytes must be a non-empty byte array.`);
  }
  if (!isNonEmptyString(value.mediaType)) {
    errors.push(`${path}.mediaType is required.`);
  }
  if (!(["semantic-source", "template-source", "native-template", "asset"] as const).includes(value.role)) {
    errors.push(`${path}.role is unsupported.`);
  }
  if (
    value.target !== undefined &&
    !(["docx", "hwpx", "pdf"] as const).includes(value.target)
  ) {
    errors.push(`${path}.target is unsupported.`);
  }
  if (value.role === "native-template" && value.target === undefined) {
    errors.push(`${path}.target is required for a native template.`);
  }
  if (value.role !== "native-template" && value.target !== undefined) {
    errors.push(`${path}.target is only allowed for native templates.`);
  }
  return errors;
}

async function hashArtifactInput(
  value: CogoDocumentArtifactInput,
): Promise<CogoDocumentHashedArtifactRef> {
  return {
    ref: value.ref,
    sha256: await sha256Bytes(value.bytes),
    mediaType: value.mediaType,
    role: value.role,
    ...(value.target ? { target: value.target } : {}),
  };
}

function parseExactJsonArtifact(
  artifact: CogoDocumentJsonArtifactInput,
  label: string,
): unknown {
  if (!isNonEmptyString(artifact.ref)) {
    throw new Error(`${label}.ref is required.`);
  }
  if (!isNonEmptyString(artifact.sourceText)) {
    throw new Error(`${label}.sourceText is required.`);
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(artifact.sourceText);
  } catch {
    throw new Error(`${label}.sourceText must be valid JSON.`);
  }
  if (
    canonicalizeDocumentTemplateJson(parsed) !==
    canonicalizeDocumentTemplateJson(artifact.value)
  ) {
    throw new Error(`${label}.value does not match its exact source bytes.`);
  }
  return parsed;
}

function snapshotExactJsonArtifact(
  artifact: CogoDocumentJsonArtifactInput,
  label: string,
): CogoDocumentJsonArtifactInput {
  const parsed = parseExactJsonArtifact(artifact, label);
  return {
    ref: artifact.ref,
    sourceText: artifact.sourceText,
    value: structuredClone(parsed),
  };
}

function snapshotArtifactInput(
  artifact: CogoDocumentArtifactInput,
  path: string,
): CogoDocumentArtifactInput {
  const errors = validateArtifactInput(artifact, path);
  if (errors.length > 0) throw new Error(errors.join(" "));
  const bytes = new Uint8Array(artifact.bytes.byteLength);
  bytes.set(artifact.bytes);
  return {
    ref: artifact.ref,
    bytes,
    mediaType: artifact.mediaType,
    role: artifact.role,
    ...(artifact.target ? { target: artifact.target } : {}),
  };
}

function snapshotSemanticEvidenceInput(
  value: CogoDocumentSemanticEvidenceInput,
  path: string,
): CogoDocumentSemanticEvidenceInput {
  if (!isRecord(value)) throw new Error(`${path} must be an object.`);
  const keyErrors: string[] = [];
  validateOnlyKeys(
    value,
    ["evidenceRef", "sourceRef", "fragmentText"],
    path,
    keyErrors,
  );
  if (keyErrors.length > 0) throw new Error(keyErrors.join(" "));
  for (const key of ["evidenceRef", "sourceRef", "fragmentText"] as const) {
    if (!isNonEmptyString(value[key])) {
      throw new Error(`${path}.${key} is required.`);
    }
  }
  return {
    evidenceRef: value.evidenceRef,
    sourceRef: value.sourceRef,
    fragmentText: value.fragmentText,
  };
}

function collectSemanticEvidenceRefs(value: unknown): string[] {
  const refs: string[] = [];
  const visit = (candidate: unknown): void => {
    if (Array.isArray(candidate)) {
      for (const entry of candidate) visit(entry);
      return;
    }
    if (!isRecord(candidate)) return;
    for (const [key, entry] of Object.entries(candidate)) {
      if (key === "evidenceRef") {
        if (!isNonEmptyString(entry)) {
          throw new Error("semantic input evidenceRef values must be non-empty strings.");
        }
        refs.push(entry);
      } else {
        visit(entry);
      }
    }
  };
  visit(value);
  return [...new Set(refs)].sort((left, right) => left.localeCompare(right));
}

function byteOccurrences(haystack: Uint8Array, needle: Uint8Array): number[] {
  const starts: number[] = [];
  for (let offset = 0; offset <= haystack.byteLength - needle.byteLength; offset += 1) {
    let matches = true;
    for (let index = 0; index < needle.byteLength; index += 1) {
      if (haystack[offset + index] !== needle[index]) {
        matches = false;
        break;
      }
    }
    if (matches) starts.push(offset);
  }
  return starts;
}

async function buildSemanticProvenance(
  semanticInput: unknown,
  evidenceInputs: CogoDocumentSemanticEvidenceInput[],
  sourceInputs: CogoDocumentArtifactInput[],
  sourceArtifacts: CogoDocumentHashedArtifactRef[],
): Promise<CogoDocumentGenerationIrV2SemanticProvenance> {
  const evidenceRefs = collectSemanticEvidenceRefs(semanticInput);
  const suppliedRefs = evidenceInputs.map((entry) => entry.evidenceRef);
  if (!unique(suppliedRefs)) {
    throw new Error("semanticEvidence evidenceRef values must be unique.");
  }
  if (
    canonicalizeDocumentTemplateJson([...suppliedRefs].sort()) !==
    canonicalizeDocumentTemplateJson(evidenceRefs)
  ) {
    throw new Error(
      "semanticEvidence must exactly cover semantic input evidenceRef values.",
    );
  }
  const sourceInputByRef = new Map(sourceInputs.map((entry) => [entry.ref, entry]));
  const sourceArtifactByRef = new Map(
    sourceArtifacts.map((entry) => [entry.ref, entry]),
  );
  const evidence = await Promise.all(
    [...evidenceInputs]
      .sort((left, right) => left.evidenceRef.localeCompare(right.evidenceRef))
      .map(async (entry) => {
        const sourceInput = sourceInputByRef.get(entry.sourceRef);
        const sourceArtifact = sourceArtifactByRef.get(entry.sourceRef);
        if (
          !sourceInput ||
          !sourceArtifact ||
          sourceInput.role !== "semantic-source" ||
          sourceArtifact.role !== "semantic-source"
        ) {
          throw new Error(
            `semanticEvidence ${entry.evidenceRef} must reference exact semantic-source bytes.`,
          );
        }
        if (!sourceInput.mediaType.startsWith("text/")) {
          throw new Error(
            `semanticEvidence ${entry.evidenceRef} requires a textual semantic source.`,
          );
        }
        const fragmentBytes = new TextEncoder().encode(entry.fragmentText);
        const occurrences = byteOccurrences(sourceInput.bytes, fragmentBytes);
        if (occurrences.length !== 1) {
          throw new Error(
            `semanticEvidence ${entry.evidenceRef} fragment must occur exactly once in ${entry.sourceRef}.`,
          );
        }
        const start = occurrences[0];
        return {
          evidenceRef: entry.evidenceRef,
          sourceRef: entry.sourceRef,
          sourceSha256: sourceArtifact.sha256,
          fragment: {
            kind: "utf8-byte-range" as const,
            start,
            end: start + fragmentBytes.byteLength,
            byteSha256: await sha256Bytes(fragmentBytes),
            expectedOccurrence: 1 as const,
          },
        };
      }),
  );
  return {
    coverage: "evidence-ref-fields-only",
    groundingMode: "declared-exact-utf8-fragment",
    semanticEntailmentVerified: false,
    evidence,
  };
}

function parseExactJsonBytes(bytes: Uint8Array, label: string): unknown {
  let sourceText: string;
  try {
    sourceText = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    throw new Error(`${label} must contain valid UTF-8 JSON bytes.`);
  }
  try {
    return JSON.parse(sourceText);
  } catch {
    throw new Error(`${label} must contain valid JSON bytes.`);
  }
}

function legacyAssetRoleForFamilyRole(role: string): string {
  return role === "brand-logo" ? "logo" : role;
}

async function jsonLineage(
  artifact: CogoDocumentJsonArtifactInput,
): Promise<CogoDocumentGenerationIrV2JsonLineage> {
  return {
    ref: artifact.ref,
    byteSha256: await sha256DocumentTemplateText(artifact.sourceText),
    canonicalSha256: await sha256CanonicalJson(artifact.value),
  };
}

function validateMargin(value: unknown, path: string, errors: string[]): void {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0 || value > 3) {
    errors.push(`${path} must be between 0 and 3.`);
  }
}

export function validateDocumentTemplateFlowPage(
  value: unknown,
  renderTargets?: readonly CogoFlowPageNativeTarget[],
): string[] {
  if (!isRecord(value)) return ["FlowPage layout must be an object."];
  const errors: string[] = [];
  // Callers that do not pass a variant's declared renderTargets (most
  // existing unit tests, which exercise the golden docx+hwpx+pdf fixture)
  // keep the strict pre-existing behavior of requiring an HWPX anchor on
  // every slot. Only a caller that explicitly declares renderTargets
  // without "hwpx" (a docx-only family, e.g. reb-report)
  // gets the anchor requirement gated off — and, symmetrically to the
  // targetOverlays if/then/else in document-template-family.cogo.schema.json,
  // an HWPX anchor becomes forbidden rather than merely optional, so a slot
  // can never carry a binding nothing will ever read.
  const requiresHwpxAnchor = renderTargets ? renderTargets.includes("hwpx") : true;
  // Every conditional requirement below is derived from what this layout
  // declares about its own sections, not from the reference family's
  // section list. A section the layout does not declare is not rendered by
  // the writers, so the style vocabulary only it could consume is rejected
  // rather than required — the same require-if-declared/forbid-if-not shape
  // the family schema uses for target overlays and the HWPX anchor above.
  const sections = declaredCogoFlowPageSections(value);
  const requiresTableStyle =
    sections.table || (renderTargets?.includes("pdf") ?? false);
  validateOnlyKeys(
    value,
    [
      "schemaVersion",
      "layoutId",
      "familyId",
      "variantId",
      "profile",
      "designPreset",
      "page",
      "staticContent",
      "styleTokenRefs",
      "resolvedStyleSheet",
      "assetPlacements",
      "slotBindings",
      "unsupportedNativePartPolicy",
    ],
    "flowPage",
    errors,
  );
  if (value.schemaVersion !== COGO_DOCUMENT_TEMPLATE_FLOW_PAGE_SCHEMA_VERSION) {
    errors.push("Unsupported FlowPage schemaVersion.");
  }
  for (const key of ["layoutId", "familyId", "variantId"] as const) {
    if (!isFlowPageIdentifier(value[key])) {
      errors.push(`${key} must be a lowercase identifier.`);
    }
  }
  if (value.profile !== "flow-page") errors.push("profile must be flow-page.");
  // designPreset is a free-form family-owned label, not a validator-enforced
  // enum: it is inherited from the v1 manifest system, is never branched on
  // again in this compiler, and the golden-only literal it used to be
  // pinned to ("standard_business_brief") would reject other admitted
  // families' own presets (e.g. standard-report's
  // "standard_report_five_section"). Presence and non-emptiness are still
  // required; the specific value is the family's business decision, not
  // this validator's.
  if (!isNonEmptyString(value.designPreset)) {
    errors.push("designPreset is required.");
  }
  if (!isRecord(value.page)) {
    errors.push("page is required.");
  } else {
    validateOnlyKeys(
      value.page,
      ["size", "flow", "marginInches"],
      "page",
      errors,
    );
    if (!(value.page.size === "letter" || value.page.size === "a4")) {
      errors.push("page.size must be letter or a4.");
    }
    if (value.page.flow !== true) errors.push("page.flow must be true.");
    if (!isRecord(value.page.marginInches)) {
      errors.push("page.marginInches is required.");
    } else {
      validateOnlyKeys(
        value.page.marginInches,
        ["top", "right", "bottom", "left"],
        "page.marginInches",
        errors,
      );
      for (const side of ["top", "right", "bottom", "left"] as const) {
        validateMargin(
          value.page.marginInches[side],
          `page.marginInches.${side}`,
          errors,
        );
      }
    }
  }
  if (!isRecord(value.staticContent)) {
    errors.push("staticContent is required.");
  } else {
    // The static-content set is the layout's own declaration of which
    // document sections it has: the base keys every writer renders, plus
    // whichever optional sections it opts into. Membership is checked
    // against that derivation, not against the reference family's six.
    validateOnlyKeys(
      value.staticContent,
      COGO_FLOW_PAGE_STATIC_CONTENT_KEYS,
      "staticContent",
      errors,
    );
    for (const key of COGO_FLOW_PAGE_BASE_STATIC_CONTENT_KEYS) {
      if (!isNonEmptyString(value.staticContent[key])) {
        errors.push(`staticContent.${key} is required.`);
      }
    }
    for (const key of COGO_FLOW_PAGE_SECTION_STATIC_CONTENT_KEYS) {
      if (key in value.staticContent && !isNonEmptyString(value.staticContent[key])) {
        errors.push(`staticContent.${key} is declared but empty.`);
      }
    }
  }
  if (
    !Array.isArray(value.styleTokenRefs) ||
    value.styleTokenRefs.length === 0 ||
    value.styleTokenRefs.some((entry) => !isNonEmptyString(entry)) ||
    !unique(value.styleTokenRefs as string[])
  ) {
    errors.push("styleTokenRefs must be a unique non-empty string list.");
  }
  if (!isRecord(value.resolvedStyleSheet)) {
    errors.push("resolvedStyleSheet is required.");
  } else {
    validateOnlyKeys(
      value.resolvedStyleSheet,
      [
        "fontFamilies",
        "colors",
        "typeScalePt",
        "spacingPt",
        // Table geometry is read while writing a table slot, and — on the
        // PDF path only — unconditionally: the PDF materialization contract
        // (apps/cogo-document-renderer/src/native-writer-contract.ts,
        // TemplatePdfMaterializationSchema.style.table, strict) requires it
        // even for a layout that emits no table node. So it is allowed
        // exactly when a declared consumer exists for it.
        ...(requiresTableStyle ? (["table"] as const) : []),
      ],
      "resolvedStyleSheet",
      errors,
    );
    const style = value.resolvedStyleSheet;
    const stringGroups: Array<{
      key: "fontFamilies" | "colors";
      fields: string[];
      // Fields allowed (and, when present, still shape-validated) but not
      // required. "accent" is read nowhere in the compiler or either
      // generated writer (docx/hwpx) — verified by grep across both
      // languages — but golden-report-workspace declares it, so it stays
      // an allowed key rather than being rejected outright.
      optionalFields?: string[];
      pattern?: RegExp;
    }> = [
      { key: "fontFamilies", fields: ["latin", "eastAsia"] },
      {
        key: "colors",
        fields: [
          "navy",
          "slate",
          "muted",
          "line",
          "soft",
          ...(sections.decisionBanner ? ["accentSoft"] : []),
          // The decision banner's cell shading is the only reader of
          // accentSoft, so it is required with a declared banner and — by
          // its absence from this list, which is also the allowed-key list —
          // rejected without one.
          "white",
        ],
        optionalFields: ["accent"],
        pattern: /^[A-F0-9]{6}$/,
      },
    ];
    for (const group of stringGroups) {
      const candidate = style[group.key];
      if (!isRecord(candidate)) {
        errors.push(`resolvedStyleSheet.${group.key} is required.`);
        continue;
      }
      const optionalFields = group.optionalFields ?? [];
      validateOnlyKeys(
        candidate,
        [...group.fields, ...optionalFields],
        `resolvedStyleSheet.${group.key}`,
        errors,
      );
      for (const field of group.fields) {
        if (
          !isNonEmptyString(candidate[field]) ||
          (group.pattern && !group.pattern.test(candidate[field] as string))
        ) {
          errors.push(`resolvedStyleSheet.${group.key}.${field} is invalid.`);
        }
      }
      for (const field of optionalFields) {
        if (field in candidate) {
          if (
            !isNonEmptyString(candidate[field]) ||
            (group.pattern && !group.pattern.test(candidate[field] as string))
          ) {
            errors.push(`resolvedStyleSheet.${group.key}.${field} is invalid.`);
          }
        }
      }
    }
    const numericGroups: Array<{
      key: "typeScalePt" | "spacingPt";
      fields: string[];
      // Steps allowed (and shape-validated when present) but not required,
      // because a declared section can consume them without any section
      // requiring them.
      optionalFields?: string[];
    }> = [
      {
        key: "typeScalePt",
        fields: [
          "title",
          "subtitle",
          "sectionHeading",
          "body",
          // Table header cells and the decision banner read tableHeader.
          ...(sections.table || sections.decisionBanner
            ? ["tableHeader"]
            : []),
          // Table body cells and the evaluation note read tableBody; with
          // neither section declared nothing can read it, so it is rejected.
          ...(sections.table || sections.evaluationNote ? ["tableBody"] : []),
          "footer",
        ],
        // The masthead eyebrow resolves through
        // COGO_FLOW_PAGE_EYEBROW_TYPE_SCALE_STEPS, so a declared tableHeader
        // is always read even by a layout with no table and no banner.
        optionalFields:
          sections.table || sections.decisionBanner ? [] : ["tableHeader"],
      },
      {
        key: "spacingPt",
        fields: ["bodyAfter", "sectionBefore", "sectionAfter"],
      },
    ];
    for (const group of numericGroups) {
      const candidate = style[group.key];
      if (!isRecord(candidate)) {
        errors.push(`resolvedStyleSheet.${group.key} is required.`);
        continue;
      }
      const optionalFields = group.optionalFields ?? [];
      validateOnlyKeys(
        candidate,
        [...group.fields, ...optionalFields],
        `resolvedStyleSheet.${group.key}`,
        errors,
      );
      for (const field of group.fields) {
        if (
          typeof candidate[field] !== "number" ||
          !Number.isFinite(candidate[field]) ||
          (candidate[field] as number) <= 0
        ) {
          errors.push(`resolvedStyleSheet.${group.key}.${field} is invalid.`);
        }
      }
      for (const field of optionalFields) {
        if (field in candidate) {
          if (
            typeof candidate[field] !== "number" ||
            !Number.isFinite(candidate[field]) ||
            (candidate[field] as number) <= 0
          ) {
            errors.push(`resolvedStyleSheet.${group.key}.${field} is invalid.`);
          }
        }
      }
    }
    if (!requiresTableStyle) {
      // Nothing reads the geometry block, so it is neither required nor
      // allowed (rejected by the resolvedStyleSheet key list above).
    } else if (!isRecord(style.table)) {
      errors.push("resolvedStyleSheet.table is required.");
    } else {
      validateOnlyKeys(
        style.table,
        ["widthDxa", "indentDxa", "cellMarginsDxa"],
        "resolvedStyleSheet.table",
        errors,
      );
      if (style.table.widthDxa !== 9360) {
        errors.push("resolvedStyleSheet.table.widthDxa must be 9360.");
      }
      if (style.table.indentDxa !== 120) {
        errors.push("resolvedStyleSheet.table.indentDxa must be 120.");
      }
      if (!isRecord(style.table.cellMarginsDxa)) {
        errors.push("resolvedStyleSheet.table.cellMarginsDxa is required.");
      } else {
        validateOnlyKeys(
          style.table.cellMarginsDxa,
          ["top", "right", "bottom", "left"],
          "resolvedStyleSheet.table.cellMarginsDxa",
          errors,
        );
        for (const side of ["top", "right", "bottom", "left"] as const) {
          const margin = style.table.cellMarginsDxa[side];
          if (typeof margin !== "number" || margin < 0) {
            errors.push(
              `resolvedStyleSheet.table.cellMarginsDxa.${side} is invalid.`,
            );
          }
        }
      }
    }
  }
  // A missing assetPlacements key is treated as an implicit empty array
  // (both current families declare zero assets and no assetPlacements key
  // at all); a present-but-wrong-shaped value is still rejected. The
  // downstream "exactly covers TemplateFamily assets" check tolerates
  // [] against zero declared assets with no further change.
  if (value.assetPlacements !== undefined && !Array.isArray(value.assetPlacements)) {
    errors.push("assetPlacements must be an array.");
  } else if (Array.isArray(value.assetPlacements)) {
    const assetIds: string[] = [];
    for (const [index, placement] of value.assetPlacements.entries()) {
      const path = `assetPlacements[${index}]`;
      if (!isRecord(placement)) {
        errors.push(`${path} must be an object.`);
        continue;
      }
      validateOnlyKeys(
        placement,
        ["assetId", "placement", "widthInches", "heightInches"],
        path,
        errors,
      );
      if (!isFlowPageIdentifier(placement.assetId)) {
        errors.push(`${path}.assetId must be a lowercase identifier.`);
      } else {
        assetIds.push(placement.assetId);
      }
      if (placement.placement !== "masthead-start") {
        errors.push(`${path}.placement is unsupported.`);
      }
      for (const dimension of ["widthInches", "heightInches"] as const) {
        if (
          typeof placement[dimension] !== "number" ||
          placement[dimension] <= 0 ||
          placement[dimension] > 10
        ) {
          errors.push(`${path}.${dimension} is invalid.`);
        }
      }
    }
    if (!unique(assetIds)) errors.push("assetPlacements asset ids must be unique.");
  }
  if (value.unsupportedNativePartPolicy !== "preserve-or-block") {
    errors.push("unsupportedNativePartPolicy must be preserve-or-block.");
  }
  if (
    !Array.isArray(value.slotBindings) ||
    value.slotBindings.length === 0 ||
    value.slotBindings.length > 900
  ) {
    errors.push("slotBindings must contain between 1 and 900 entries.");
    return errors;
  }

  const slotIds: string[] = [];
  const nodeIds: string[] = [];
  const docxAnchors: string[] = [];
  const hwpxAnchors: string[] = [];
  for (const [index, rawBinding] of value.slotBindings.entries()) {
    const path = `slotBindings[${index}]`;
    if (!isRecord(rawBinding)) {
      errors.push(`${path} must be an object.`);
      continue;
    }
    validateOnlyKeys(
      rawBinding,
      [
        "slotId",
        "nodeId",
        "semanticRole",
        "inputBindingPath",
        "heading",
        "styleTokenRefs",
        "slotKind",
        "columns",
        "targetAnchors",
      ],
      path,
      errors,
    );
    for (const key of ["semanticRole", "heading"] as const) {
      if (!isNonEmptyString(rawBinding[key])) {
        errors.push(`${path}.${key} is required.`);
      }
    }
    if (!isFlowPageSlotIdentifier(rawBinding.slotId)) {
      errors.push(`${path}.slotId must be a lowercase identifier of at most 27 characters.`);
    }
    if (!isFlowPageIdentifier(rawBinding.nodeId)) {
      errors.push(`${path}.nodeId must be a lowercase identifier.`);
    } else if (
      rawBinding.nodeId === "static" ||
      rawBinding.nodeId.startsWith("static.") ||
      rawBinding.nodeId.startsWith("static-") ||
      rawBinding.nodeId === "asset" ||
      rawBinding.nodeId.startsWith("asset.") ||
      rawBinding.nodeId.startsWith("asset-")
    ) {
      errors.push(`${path}.nodeId uses a generated-node namespace.`);
    }
    if (isNonEmptyString(rawBinding.slotId)) slotIds.push(rawBinding.slotId);
    if (isNonEmptyString(rawBinding.nodeId)) nodeIds.push(rawBinding.nodeId);
    if (
      !isNonEmptyString(rawBinding.inputBindingPath) ||
      !/^\/(?:[^/~]|~[01])+(?:\/(?:[^/~]|~[01])+)*$/.test(
        rawBinding.inputBindingPath,
      )
    ) {
      errors.push(`${path}.inputBindingPath must be a JSON pointer.`);
    }
    if (!(rawBinding.slotKind === "rich-text" || rawBinding.slotKind === "table")) {
      errors.push(`${path}.slotKind is unsupported.`);
    }
    if (
      !Array.isArray(rawBinding.styleTokenRefs) ||
      rawBinding.styleTokenRefs.length === 0 ||
      rawBinding.styleTokenRefs.some((entry) => !isNonEmptyString(entry)) ||
      !unique(rawBinding.styleTokenRefs as string[])
    ) {
      errors.push(`${path}.styleTokenRefs must be a unique non-empty string list.`);
    }
    if (!isRecord(rawBinding.targetAnchors)) {
      errors.push(`${path}.targetAnchors is required.`);
    } else {
      validateOnlyKeys(
        rawBinding.targetAnchors,
        requiresHwpxAnchor ? ["docx", "hwpx"] : ["docx"],
        `${path}.targetAnchors`,
        errors,
      );
      const docx = rawBinding.targetAnchors.docx;
      if (isRecord(docx)) {
        validateOnlyKeys(
          docx,
          ["kind", "part", "value", "expectedOccurrence"],
          `${path}.targetAnchors.docx`,
          errors,
        );
      }
      if (
        !isRecord(docx) ||
        docx.kind !== "bookmark" ||
        docx.part !== "word/document.xml" ||
        !isNonEmptyString(docx.value) ||
        !/^[A-Za-z][A-Za-z0-9_]{0,39}$/.test(docx.value) ||
        docx.value.startsWith("cogo_static_") ||
        docx.value.startsWith("cogo_heading_") ||
        docx.expectedOccurrence !== 1
      ) {
        errors.push(
          `${path}.targetAnchors.docx must bind one Word-safe bookmark in word/document.xml.`,
        );
      } else {
        docxAnchors.push(`${docx.part}:${docx.value}`);
      }
      if (requiresHwpxAnchor) {
        const hwpx = rawBinding.targetAnchors.hwpx;
        if (isRecord(hwpx)) {
          validateOnlyKeys(
            hwpx,
            ["kind", "part", "value", "expectedOccurrence"],
            `${path}.targetAnchors.hwpx`,
            errors,
          );
        }
        const expectedHwpxKind =
          rawBinding.slotKind === "table" ? "table-id" : "paragraph-id";
        if (
          !isRecord(hwpx) ||
          hwpx.kind !== expectedHwpxKind ||
          hwpx.part !== "Contents/section0.xml" ||
          !isNonEmptyString(hwpx.value) ||
          !/^[1-8]\d{3}$/.test(hwpx.value) ||
          hwpx.expectedOccurrence !== 1
        ) {
          errors.push(
            `${path}.targetAnchors.hwpx must bind one ${expectedHwpxKind} with a numeric id in Contents/section0.xml.`,
          );
        } else {
          hwpxAnchors.push(
            `${hwpx.part}:${hwpx.kind}:${hwpx.value}`,
          );
        }
      }
      // requiresHwpxAnchor === false: the validateOnlyKeys(["docx"]) call
      // above already rejects a present targetAnchors.hwpx as "not
      // allowed" — nothing here reads it, so this branch neither requires
      // nor separately re-validates it.
    }
    if (rawBinding.slotKind === "table") {
      if (!Array.isArray(rawBinding.columns) || rawBinding.columns.length === 0) {
        errors.push(`${path}.columns must be non-empty for a table slot.`);
      } else {
        const keys: string[] = [];
        let width = 0;
        for (const [columnIndex, rawColumn] of rawBinding.columns.entries()) {
          if (!isRecord(rawColumn)) {
            errors.push(`${path}.columns[${columnIndex}] must be an object.`);
            continue;
          }
          validateOnlyKeys(
            rawColumn,
            ["key", "label", "widthRatio"],
            `${path}.columns[${columnIndex}]`,
            errors,
          );
          if (!isFlowPageBindingKey(rawColumn.key)) {
            errors.push(`${path}.columns[${columnIndex}].key is invalid.`);
          } else {
            keys.push(rawColumn.key);
          }
          if (!isNonEmptyString(rawColumn.label)) {
            errors.push(`${path}.columns[${columnIndex}].label is required.`);
          }
          if (
            typeof rawColumn.widthRatio !== "number" ||
            rawColumn.widthRatio <= 0 ||
            rawColumn.widthRatio > 1
          ) {
            errors.push(`${path}.columns[${columnIndex}].widthRatio is invalid.`);
          } else {
            width += rawColumn.widthRatio;
          }
        }
        if (!unique(keys)) errors.push(`${path}.column keys must be unique.`);
        if (Math.abs(width - 1) > 0.0001) {
          errors.push(`${path}.column widthRatio values must sum to 1.`);
        }
      }
    } else if (rawBinding.columns !== undefined) {
      errors.push(`${path}.columns is only allowed for a table slot.`);
    }
  }
  if (!unique(slotIds)) errors.push("FlowPage slot ids must be unique.");
  if (!unique(nodeIds)) errors.push("FlowPage node ids must be unique.");
  if (!unique(docxAnchors)) errors.push("DOCX bookmark anchors must be unique.");
  if (!unique(hwpxAnchors)) errors.push("HWPX native anchors must be unique.");
  return [...new Set(errors)];
}

function flowVariant(
  family: CogoDocumentTemplateFamily,
  variantId: string,
): Extract<CogoDocumentTemplateFamilyVariant, { layoutProfile: "flow-page" }> {
  const variant = family.variants.find((entry) => entry.variantId === variantId);
  if (!variant) throw new Error(`Unknown template family variant: ${variantId}.`);
  if (variant.layoutProfile !== "flow-page") {
    throw new Error(`Variant ${variantId} is not a flow-page variant.`);
  }
  return variant;
}

function bindFlowPageSlots(
  layout: CogoDocumentTemplateFlowPage,
  semanticInput: unknown,
): Record<string, unknown> {
  const bindings: Record<string, unknown> = {};
  for (const slot of layout.slotBindings) {
    const value = resolveDocumentTemplatePointer(
      semanticInput,
      slot.inputBindingPath,
    );
    if (slot.slotKind === "rich-text") {
      if (!isNonEmptyString(value)) {
        throw new Error(`Slot ${slot.slotId} requires non-empty rich text.`);
      }
    } else {
      if (!Array.isArray(value) || value.length === 0) {
        throw new Error(`Slot ${slot.slotId} requires at least one table row.`);
      }
      for (const [rowIndex, row] of value.entries()) {
        if (!isRecord(row)) {
          throw new Error(`Slot ${slot.slotId} row ${rowIndex} must be an object.`);
        }
        for (const column of slot.columns) {
          if (!isNonEmptyString(row[column.key])) {
            throw new Error(
              `Slot ${slot.slotId} row ${rowIndex} requires ${column.key}.`,
            );
          }
        }
      }
    }
    bindings[slot.slotId] = structuredClone(value);
  }
  return bindings;
}

function validateFamilyLayoutContract(
  family: CogoDocumentTemplateFamily,
  variant: Extract<CogoDocumentTemplateFamilyVariant, { layoutProfile: "flow-page" }>,
  layout: CogoDocumentTemplateFlowPage,
  refs: {
    familyRef: string;
    layoutRef: string;
    layoutByteSha256: string;
    inputSchemaRef: string;
    inputSchemaByteSha256: string;
  },
): void {
  if (layout.familyId !== family.familyId) {
    throw new Error("FlowPage familyId does not match TemplateFamily.");
  }
  if (layout.variantId !== variant.variantId) {
    throw new Error("FlowPage variantId does not match the selected variant.");
  }
  const expectedLayoutRef = resolveDeclaredRef(
    refs.familyRef,
    variant.layout.layoutRef,
  );
  if (refs.layoutRef !== expectedLayoutRef) {
    throw new Error(
      `FlowPage ref ${refs.layoutRef} does not match variant layoutRef ${expectedLayoutRef}.`,
    );
  }
  if (!variant.layout.layoutSha256) {
    throw new Error(
      "FlowPage variant must pin layoutSha256 for Generation IR v2.",
    );
  }
  if (refs.layoutByteSha256 !== variant.layout.layoutSha256) {
    throw new Error("FlowPage byte hash does not match variant layoutSha256.");
  }
  const expectedInputSchemaRef = resolveDeclaredRef(
    refs.familyRef,
    family.semanticCore.dataSchema.ref,
  );
  if (refs.inputSchemaRef !== expectedInputSchemaRef) {
    throw new Error(
      `Input schema ref ${refs.inputSchemaRef} does not match TemplateFamily dataSchema ${expectedInputSchemaRef}.`,
    );
  }
  if (
    !family.semanticCore.dataSchema.sha256 ||
    refs.inputSchemaByteSha256 !== family.semanticCore.dataSchema.sha256
  ) {
    throw new Error("Input schema byte hash does not match TemplateFamily dataSchema.");
  }
  const familySlots = new Map(
    family.semanticCore.slots.map((slot) => [slot.id, slot]),
  );
  const layoutSlots = new Map(layout.slotBindings.map((slot) => [slot.slotId, slot]));
  for (const slot of family.semanticCore.slots) {
    const bound = layoutSlots.get(slot.id);
    const omitted = variant.approvedOmissions.some((entry) => entry.slotId === slot.id);
    if (slot.required && !bound && !omitted) {
      throw new Error(`Required shared slot ${slot.id} is not bound by FlowPage.`);
    }
  }
  for (const binding of layout.slotBindings) {
    const shared = familySlots.get(binding.slotId);
    if (!shared) {
      throw new Error(`FlowPage slot ${binding.slotId} is not declared by TemplateFamily.`);
    }
    if (shared.kind !== binding.slotKind) {
      throw new Error(
        `FlowPage slot ${binding.slotId} kind ${binding.slotKind} does not match shared kind ${shared.kind}.`,
      );
    }
    if (shared.semanticRole !== binding.semanticRole) {
      throw new Error(
        `FlowPage slot ${binding.slotId} semanticRole does not match TemplateFamily.`,
      );
    }
    const variantBinding = variant.slotBindings.find(
      (entry) => entry.slotId === binding.slotId,
    );
    if (!variantBinding) {
      throw new Error(`Variant does not bind FlowPage slot ${binding.slotId}.`);
    }
    const [bindingResourceRef, bindingFragment = ""] =
      variantBinding.bindingRef.split("#", 2);
    const resolvedBindingRef = resolveDeclaredRef(
      refs.familyRef,
      bindingResourceRef,
    );
    if (resolvedBindingRef !== refs.layoutRef || !bindingFragment.startsWith("/")) {
      throw new Error(
        `Variant binding for ${binding.slotId} must point into the exact FlowPage resource.`,
      );
    }
    let pointed: unknown;
    try {
      pointed = resolveResourcePointer(layout, bindingFragment);
    } catch {
      throw new Error(
        `Variant binding for ${binding.slotId} does not resolve in FlowPage.`,
      );
    }
    if (!isRecord(pointed) || pointed.slotId !== binding.slotId) {
      throw new Error(
        `Variant binding for ${binding.slotId} resolves to a different FlowPage slot.`,
      );
    }
  }
  for (const variantBinding of variant.slotBindings) {
    const layoutBinding = layoutSlots.get(variantBinding.slotId);
    if (!layoutBinding) {
      throw new Error(
        `Variant binding ${variantBinding.slotId} has no exact FlowPage slot.`,
      );
    }
    const [bindingResourceRef, bindingFragment = ""] =
      variantBinding.bindingRef.split("#", 2);
    const resolvedBindingRef = resolveDeclaredRef(
      refs.familyRef,
      bindingResourceRef,
    );
    if (resolvedBindingRef !== refs.layoutRef || !bindingFragment.startsWith("/")) {
      throw new Error(
        `Variant binding for ${variantBinding.slotId} must point into the exact FlowPage resource.`,
      );
    }
    let pointed: unknown;
    try {
      pointed = resolveResourcePointer(layout, bindingFragment);
    } catch {
      throw new Error(
        `Variant binding for ${variantBinding.slotId} does not resolve in FlowPage.`,
      );
    }
    if (pointed !== layoutBinding) {
      throw new Error(
        `Variant binding for ${variantBinding.slotId} does not resolve to its exact FlowPage slot.`,
      );
    }
  }
}

function resolveFamilyResourceArtifacts(
  family: CogoDocumentTemplateFamily,
  familyRef: string,
  layout: CogoDocumentTemplateFlowPage,
  layoutLineage: CogoDocumentGenerationIrV2JsonLineage,
  inputSchemaLineage: CogoDocumentGenerationIrV2JsonLineage,
  sourceArtifacts: CogoDocumentHashedArtifactRef[],
  sourceArtifactInputs: CogoDocumentArtifactInput[],
): CogoDocumentGenerationIrV2ResolvedAsset[] {
  const artifactsByRef = new Map(
    sourceArtifacts.map((artifact) => [artifact.ref, artifact]),
  );
  const artifactInputsByRef = new Map(
    sourceArtifactInputs.map((artifact) => [artifact.ref, artifact]),
  );
  const parsedTemplateSources = new Map<string, unknown>();
  const authoritativeRefs = new Set([
    layoutLineage.ref,
    inputSchemaLineage.ref,
    ...artifactsByRef.keys(),
  ]);
  for (const sourceRef of family.semanticCore.sourceProvenance.sourceRefs) {
    const resolved = resolveDeclaredRef(familyRef, sourceRef);
    if (!authoritativeRefs.has(resolved)) {
      throw new Error(
        `TemplateFamily source provenance ${resolved} has no exact artifact bytes.`,
      );
    }
  }
  for (const legacyRef of family.legacyTemplateRefs) {
    const resolved = resolveDeclaredRef(familyRef, legacyRef.manifestRef);
    const artifact = artifactsByRef.get(resolved);
    if (
      !artifact ||
      artifact.role !== "template-source" ||
      artifact.sha256 !== legacyRef.manifestSha256
    ) {
      throw new Error(
        `Legacy template ${resolved} is missing or does not match manifestSha256.`,
      );
    }
    if (artifact.mediaType !== "application/json") {
      throw new Error(`Legacy template ${resolved} must be JSON.`);
    }
    const sourceInput = artifactInputsByRef.get(resolved);
    if (!sourceInput) {
      throw new Error(`Legacy template ${resolved} has no exact source bytes.`);
    }
    const manifest = parseExactJsonBytes(
      sourceInput.bytes,
      `Legacy template ${resolved}`,
    );
    const manifestErrors = validateDocumentTemplateManifest(manifest);
    if (manifestErrors.length > 0) {
      throw new Error(
        `Legacy template ${resolved} is not an admitted v1 manifest: ${manifestErrors.join(" ")}`,
      );
    }
    parsedTemplateSources.set(resolved, manifest);
    const admittedManifest = manifest as CogoDocumentTemplateManifest;
    if (
      admittedManifest.schemaVersion !== legacyRef.schemaVersion ||
      admittedManifest.templateId !== legacyRef.templateId ||
      admittedManifest.templateVersion !== legacyRef.templateVersion
    ) {
      throw new Error(
        `Legacy template ${resolved} identity does not match TemplateFamily legacyTemplateRef.`,
      );
    }
    if (
      canonicalizeDocumentTemplateJson(admittedManifest.staticContent) !==
        canonicalizeDocumentTemplateJson(layout.staticContent)
    ) {
      throw new Error(
        `Legacy template ${resolved} staticContent does not match FlowPage.`,
      );
    }
    const legacyLayout = admittedManifest.layout;
    if (
      legacyLayout.preset !== layout.designPreset ||
      legacyLayout.pageSize !== layout.page.size ||
      canonicalizeDocumentTemplateJson(legacyLayout.marginInches) !==
        canonicalizeDocumentTemplateJson(layout.page.marginInches) ||
      canonicalizeDocumentTemplateJson(legacyLayout.slotOrder) !==
        canonicalizeDocumentTemplateJson(
          layout.slotBindings.map((binding) => binding.slotId),
        )
    ) {
      throw new Error(
        `Legacy template ${resolved} layout does not match FlowPage.`,
      );
    }
    if (admittedManifest.assets.length !== family.semanticCore.assets.length) {
      throw new Error(
        `Legacy template ${resolved} assets do not exactly cover TemplateFamily assets.`,
      );
    }
    const legacyAssetKeys = admittedManifest.assets.map((asset) =>
      `${resolveDeclaredRef(resolved, asset.path)}:${asset.role}`,
    );
    const familyAssetKeys = family.semanticCore.assets.map((asset) =>
      `${resolveDeclaredRef(familyRef, asset.sourceRef)}:${legacyAssetRoleForFamilyRole(asset.role)}`,
    );
    if (
      canonicalizeDocumentTemplateJson([...legacyAssetKeys].sort()) !==
      canonicalizeDocumentTemplateJson([...familyAssetKeys].sort())
    ) {
      throw new Error(
        `Legacy template ${resolved} asset path or role does not match TemplateFamily.`,
      );
    }
    for (const legacyAsset of admittedManifest.assets) {
      if (!legacyAsset.sha256) continue;
      const legacyAssetRef = resolveDeclaredRef(resolved, legacyAsset.path);
      const familyAsset = family.semanticCore.assets.find(
        (asset) => resolveDeclaredRef(familyRef, asset.sourceRef) === legacyAssetRef,
      );
      if (!familyAsset?.sha256 || familyAsset.sha256 !== legacyAsset.sha256) {
        throw new Error(
          `Legacy template ${resolved} asset ${legacyAsset.path} hash does not match TemplateFamily.`,
        );
      }
    }
  }
  const layoutSlots = new Map(
    layout.slotBindings.map((binding) => [binding.slotId, binding]),
  );
  for (const slot of family.semanticCore.slots) {
    if (!("slotRef" in slot)) continue;
    const [resourceRef, fragment = ""] = slot.slotRef.split("#", 2);
    const resolved = resolveDeclaredRef(familyRef, resourceRef);
    const resource = parsedTemplateSources.get(resolved);
    if (!resource || !fragment.startsWith("/")) {
      throw new Error(
        `Shared slot ${slot.id} does not reference an exact legacy template fragment.`,
      );
    }
    let legacySlot: unknown;
    try {
      legacySlot = resolveResourcePointer(resource, fragment);
    } catch {
      throw new Error(`Shared slot ${slot.id} legacy slotRef does not resolve.`);
    }
    if (
      !isRecord(legacySlot) ||
      legacySlot.id !== slot.id ||
      legacySlot.kind !== slot.kind ||
      legacySlot.required !== slot.required
    ) {
      throw new Error(
        `Shared slot ${slot.id} does not match its exact legacy template fragment.`,
      );
    }
    const layoutSlot = layoutSlots.get(slot.id);
    if (!layoutSlot) continue;
    if (
      legacySlot.bindingPath !== layoutSlot.inputBindingPath ||
      legacySlot.heading !== layoutSlot.heading ||
      (layoutSlot.slotKind === "table" &&
        canonicalizeDocumentTemplateJson(legacySlot.columns) !==
          canonicalizeDocumentTemplateJson(layoutSlot.columns))
    ) {
      throw new Error(
        `FlowPage slot ${slot.id} does not match its exact legacy template fragment.`,
      );
    }
  }
  const familyAssets = new Map(
    family.semanticCore.assets.map((asset) => [asset.id, asset]),
  );
  for (const asset of family.semanticCore.assets) {
    const resolved = resolveDeclaredRef(familyRef, asset.sourceRef);
    const artifact = artifactsByRef.get(resolved);
    if (
      !artifact ||
      artifact.role !== "asset" ||
      artifact.mediaType !== asset.mediaType ||
      !asset.sha256 ||
      artifact.sha256 !== asset.sha256
    ) {
      throw new Error(
        `TemplateFamily asset ${asset.id} is missing exact bytes or hash binding.`,
      );
    }
  }
  for (const placement of layout.assetPlacements) {
    const asset = familyAssets.get(placement.assetId);
    if (!asset) {
      throw new Error(
        `FlowPage asset placement ${placement.assetId} is not declared by TemplateFamily.`,
      );
    }
  }
  if (
    canonicalizeDocumentTemplateJson(
      layout.assetPlacements.map((placement) => placement.assetId).sort(),
    ) !==
    canonicalizeDocumentTemplateJson(
      family.semanticCore.assets.map((asset) => asset.id).sort(),
    )
  ) {
    throw new Error(
      "FlowPage assetPlacements must exactly cover TemplateFamily assets.",
    );
  }
  return layout.assetPlacements.map((placement) => {
    const asset = familyAssets.get(placement.assetId);
    if (!asset) {
      throw new Error(
        `FlowPage asset placement ${placement.assetId} is not declared by TemplateFamily.`,
      );
    }
    const artifact = artifactsByRef.get(
      resolveDeclaredRef(familyRef, asset.sourceRef),
    );
    if (!artifact || artifact.role !== "asset") {
      throw new Error(
        `FlowPage asset placement ${placement.assetId} has no exact asset artifact.`,
      );
    }
    return {
      assetId: asset.id,
      role: asset.role,
      artifact: { ...artifact, role: "asset" as const },
      placement: structuredClone(placement),
    };
  });
}

function targetContentType(target: CogoFlowPageNativeTarget): string {
  if (target === "docx") {
    return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  }
  return target === "hwpx" ? "application/hwp+zip" : "application/pdf";
}

function requiredPackageParts(target: CogoFlowPageNativeTarget): string[] {
  if (target === "pdf") return [];
  if (target === "docx") {
    return [
      "[Content_Types].xml",
      "_rels/.rels",
      "docProps/app.xml",
      "docProps/core.xml",
      "word/document.xml",
      "word/footer1.xml",
      "word/styles.xml",
      "word/_rels/document.xml.rels",
    ];
  }
  return [
    "mimetype",
    "version.xml",
    "META-INF/container.xml",
    "META-INF/manifest.xml",
    "Contents/content.hpf",
    "Contents/header.xml",
    "Contents/section0.xml",
    "settings.xml",
    "Preview/PrvText.txt",
  ];
}

function writerId(target: CogoFlowPageNativeTarget): string {
  if (target === "docx") return "cogo.flow-page-docx-writer";
  return target === "hwpx"
    ? "cogo.flow-page-hwpx-writer"
    : "cogo.flow-page-pdf-writer";
}

function readerId(target: CogoFlowPageNativeTarget): string {
  if (target === "docx") return "cogo.flow-page-docx-reader";
  return target === "hwpx"
    ? "cogo.flow-page-hwpx-reader"
    : "cogo.flow-page-pdf-reader";
}

function staticAnchor(
  target: CogoFlowPageNativeTarget,
  key: CogoFlowPageStaticContentKey,
): CogoFlowPageDocxAnchor | CogoFlowPageHwpxAnchor | CogoFlowPagePdfAnchor {
  if (target === "docx") {
    return {
      kind: "bookmark",
      part: key === "footer" ? "word/footer1.xml" : "word/document.xml",
      value: `cogo_static_${key.replaceAll(/([A-Z])/g, "_$1").toLowerCase()}`,
      expectedOccurrence: 1,
    };
  }
  if (target === "pdf") {
    return {
      kind: "named-destination",
      structureType: key === "title" ? "H1" : "P",
      value: `cogo_static_${key.replaceAll(/([A-Z])/g, "_$1").toLowerCase()}`,
      expectedOccurrence: 1,
      pageAssociationRequired: true,
    };
  }
  const ids: Record<CogoFlowPageStaticContentKey, string> = {
    eyebrow: "10",
    title: "11",
    subtitle: "12",
    decisionLabel: "13",
    evaluationNote: "9001",
    footer: "9002",
  };
  return {
    kind: "paragraph-id",
    part: "Contents/section0.xml",
    value: ids[key],
    expectedOccurrence: 1,
  };
}

function headingAnchor(
  target: CogoFlowPageNativeTarget,
  slot: CogoFlowPageSlotBinding,
  slotIndex: number,
): CogoFlowPageDocxAnchor | CogoFlowPageHwpxAnchor | CogoFlowPagePdfAnchor {
  if (target === "docx") {
    return {
      kind: "bookmark",
      part: "word/document.xml",
      value: `cogo_heading_${slot.slotId.replaceAll(/[.-]/g, "_")}`,
      expectedOccurrence: 1,
    };
  }
  if (target === "pdf") {
    return {
      kind: "named-destination",
      structureType: "H2",
      value: `cogo_heading_${slot.slotId.replaceAll(/[.-]/g, "_")}`,
      expectedOccurrence: 1,
      pageAssociationRequired: true,
    };
  }
  return {
    kind: "paragraph-id",
    part: "Contents/section0.xml",
    value: String(100 + slotIndex),
    expectedOccurrence: 1,
  };
}

function pdfSlotAnchor(slot: CogoFlowPageSlotBinding): CogoFlowPagePdfAnchor {
  return {
    kind: "named-destination",
    structureType: slot.slotKind === "table" ? "Table" : "P",
    value: `cogo_slot_${slot.slotId.replaceAll(/[.-]/g, "_")}`,
    expectedOccurrence: 1,
    pageAssociationRequired: true,
  };
}

/**
 * Canonical PDF semantic text normalization. Tagged-PDF readers must apply the
 * same transform to structure text before hashing: NFC, canonical LF, collapse
 * horizontal spacing inside each line, trim lines, and omit empty lines.
 */
export function normalizeCogoFlowPagePdfText(value: string): string {
  return value
    .normalize("NFC")
    .replaceAll(/\r\n?/g, "\n")
    .split("\n")
    .map((line) => line.replaceAll(/[\t\f\v\u00a0 ]+/g, " ").trim())
    .filter((line) => line.length > 0)
    .join("\n");
}

export function buildCogoFlowPagePdfTableValueBasis(
  columns: readonly CogoFlowPageTableColumn[],
  rows: readonly Record<string, string>[],
): string[][] {
  return [
    columns.map((column) =>
      normalizeCogoFlowPagePdfText(column.label),
    ),
    ...rows.map((row) =>
      columns.map((column) =>
        normalizeCogoFlowPagePdfText(row[column.key]),
      ),
    ),
  ];
}

export function canonicalizeCogoFlowPagePdfSemanticValue(
  value: string | readonly (readonly string[])[],
): string {
  return canonicalizeDocumentTemplateJson(value);
}

function pdfSlotValueBasis(
  slot: CogoFlowPageSlotBinding,
  value: unknown,
): string | string[][] {
  if (slot.slotKind === "rich-text") {
    return normalizeCogoFlowPagePdfText(value as string);
  }
  return buildCogoFlowPagePdfTableValueBasis(
    slot.columns,
    value as Array<Record<string, string>>,
  );
}

function assetAnchor(
  target: CogoFlowPageNativeTarget,
  asset: CogoDocumentGenerationIrV2ResolvedAsset,
  assetIndex: number,
): CogoFlowPageDocxAssetAnchor | CogoFlowPageHwpxAssetAnchor {
  if (target === "pdf") {
    throw new Error(
      "PDF FlowPage assets are blocked until an exact image XObject materialization locator and deterministic asset transform are defined.",
    );
  }
  if (asset.artifact.mediaType !== "image/svg+xml") {
    throw new Error(
      `Generated FlowPage asset ${asset.assetId} must be SVG until target-specific identity package locators are defined for ${asset.artifact.mediaType}.`,
    );
  }
  if (target === "docx") {
    const extentEmu = {
      width: Math.round(asset.placement.widthInches * 914400),
      height: Math.round(asset.placement.heightInches * 914400),
    };
    if (extentEmu.width < 1 || extentEmu.height < 1) {
      throw new Error(
        `Generated FlowPage asset ${asset.assetId} has a rounded DOCX extent below one EMU.`,
      );
    }
    const relationshipId = `rIdCogoSvg${assetIndex + 1}`;
    return {
      kind: "package-relationship",
      part: "word/document.xml",
      relationshipsPart: "word/_rels/document.xml.rels",
      relationshipId,
      relationshipType:
        "http://schemas.openxmlformats.org/officeDocument/2006/relationships/image",
      packagePart: `word/media/cogo-${asset.assetId}-${assetIndex + 1}.svg`,
      expectedOccurrence: 1,
      contentType: {
        kind: "extension-content-type",
        part: "[Content_Types].xml",
        extension: "svg",
        mediaType: "image/svg+xml",
        expectedOccurrence: 1,
      },
      renderNode: {
        kind: "svg-blip",
        part: "word/document.xml",
        relationshipId,
        expectedOccurrence: 1,
        extentEmu,
        container: {
          kind: "table-cell",
          tableIndex: 0,
          rowIndex: 0,
          cellIndex: 0,
        },
      },
    };
  }
  const extentHwp = {
    width: Math.round(asset.placement.widthInches * 7200),
    height: Math.round(asset.placement.heightInches * 7200),
  };
  if (extentHwp.width < 1 || extentHwp.height < 1) {
    throw new Error(
      `Generated FlowPage asset ${asset.assetId} has a rounded HWPX extent below one HWP unit.`,
    );
  }
  return {
    kind: "package-manifest-item",
    part: "Contents/content.hpf",
    manifestItemId: `asset${assetIndex + 1}`,
    packagePart: `BinData/${asset.assetId}.svg`,
    mediaType: asset.artifact.mediaType,
    expectedOccurrence: 1,
    renderNode: {
      kind: "image-binary-ref",
      part: "Contents/section0.xml",
      binaryItemId: `asset${assetIndex + 1}`,
      expectedOccurrence: 1,
      extentHwp,
      container: {
        kind: "paragraph-id",
        value: "14",
        relation: "before",
        relativeToParagraphId: "10",
      },
    },
  };
}

function docxFooterLink(): CogoFlowPageDocxFooterLink {
  return {
    kind: "footer-reference",
    documentPart: "word/document.xml",
    relationshipsPart: "word/_rels/document.xml.rels",
    relationshipId: "rIdCogoFooter1",
    relationshipType:
      "http://schemas.openxmlformats.org/officeDocument/2006/relationships/footer",
    targetPart: "word/footer1.xml",
    expectedOccurrence: 1,
    renderNode: {
      kind: "section-footer-reference",
      part: "word/document.xml",
      referenceType: "default",
      relationshipId: "rIdCogoFooter1",
      expectedOccurrence: 1,
    },
  };
}

function nativeLocatorKey(
  anchor:
    | CogoFlowPageDocxAnchor
    | CogoFlowPageHwpxAnchor
    | CogoFlowPagePdfAnchor
    | CogoFlowPageDocxAssetAnchor
    | CogoFlowPageHwpxAssetAnchor,
): string[] {
  if (anchor.kind === "bookmark") {
    return [`${anchor.part}:bookmark:${anchor.value}`];
  }
  if (anchor.kind === "paragraph-id" || anchor.kind === "table-id") {
    return [`${anchor.part}:${anchor.kind}:${anchor.value}`];
  }
  if (anchor.kind === "named-destination") {
    return [
      `pdf:destination:${anchor.value}`,
      `pdf:structure:${anchor.structureType}:${anchor.value}`,
    ];
  }
  if (anchor.kind === "package-relationship") {
    return [
      `${anchor.relationshipsPart}:relationship:${anchor.relationshipId}`,
      `${anchor.renderNode.part}:${anchor.renderNode.kind}:${anchor.renderNode.relationshipId}`,
      `${anchor.contentType.part}:${anchor.contentType.kind}:${anchor.contentType.extension}:${anchor.contentType.mediaType}`,
      `${anchor.renderNode.part}:${anchor.renderNode.container.kind}:${anchor.renderNode.container.tableIndex}:${anchor.renderNode.container.rowIndex}:${anchor.renderNode.container.cellIndex}`,
      `package:${anchor.packagePart}`,
    ];
  }
  return [
    `${anchor.part}:manifest:${anchor.manifestItemId}:${anchor.packagePart}`,
    `${anchor.renderNode.part}:${anchor.renderNode.kind}:${anchor.renderNode.binaryItemId}`,
    `${anchor.renderNode.part}:${anchor.renderNode.container.kind}:${anchor.renderNode.container.value}`,
    `package:${anchor.packagePart}`,
  ];
}

function assertUniqueTargetLocators(
  target: CogoFlowPageNativeTarget,
  operations: CogoDocumentGenerationIrV2Operation[],
): void {
  const operationIds = operations.map((operation) => operation.operationId);
  if (!unique(operationIds)) {
    throw new Error(`${target} write plan operation ids must be globally unique.`);
  }
  const nodeIds = operations.map((operation) => operation.nodeId);
  if (!unique(nodeIds)) {
    throw new Error(`${target} write plan node ids must be globally unique.`);
  }
  const locators: string[] = [];
  for (const operation of operations) {
    locators.push(...nativeLocatorKey(operation.anchor));
    if (operation.bindingKind === "slot") {
      locators.push(...nativeLocatorKey(operation.heading.anchor));
    }
    if (operation.bindingKind === "static" && operation.documentLink) {
      locators.push(
        `${operation.documentLink.relationshipsPart}:relationship:${operation.documentLink.relationshipId}`,
        `${operation.documentLink.renderNode.part}:${operation.documentLink.renderNode.kind}:${operation.documentLink.renderNode.referenceType}:${operation.documentLink.renderNode.relationshipId}`,
      );
    }
  }
  if (!unique(locators)) {
    throw new Error(
      `${target} write plan native locators must be globally unique across slot, heading, static, asset, and document-link bindings.`,
    );
  }
}

/**
 * Table geometry for a layout that declares a table slot.
 * validateDocumentTemplateFlowPage requires the block for exactly those
 * layouts and rejects it for the rest, so this throws rather than inventing
 * a default: a table slot whose geometry is missing is a contract break,
 * not a shape to guess at.
 */
function requireFlowPageTableStyle(
  layout: CogoDocumentTemplateFlowPage,
  slotId: string,
): NonNullable<CogoDocumentTemplateFlowPage["resolvedStyleSheet"]["table"]> {
  const table = layout.resolvedStyleSheet.table;
  if (!table) {
    throw new Error(
      `FlowPage slot ${slotId} declares a table but the layout declares no resolvedStyleSheet.table geometry.`,
    );
  }
  return table;
}

async function buildOperations(
  target: CogoFlowPageNativeTarget,
  layout: CogoDocumentTemplateFlowPage,
  bindings: Record<string, unknown>,
  resolvedAssets: CogoDocumentGenerationIrV2ResolvedAsset[],
): Promise<CogoDocumentGenerationIrV2Operation[]> {
  if (target === "pdf" && resolvedAssets.length > 0) {
    throw new Error(
      "PDF FlowPage assets are blocked until an exact image XObject materialization locator and deterministic asset transform are defined.",
    );
  }
  // One static operation per DECLARED static key, in canonical vocabulary
  // order: a layout that declares no decision banner gets no banner
  // operation, and every writer that walks this plan therefore renders none.
  const staticOperations = await Promise.all(
    declaredCogoFlowPageSections(layout).staticContentKeys.map(async (key) => {
      const declaredValue = layout.staticContent[key] as string;
      const valueBasis =
        target === "pdf"
          ? normalizeCogoFlowPagePdfText(declaredValue)
          : declaredValue;
      return {
        operationId: `${target}:static:${key}`,
        target,
        bindingKind: "static" as const,
        bindingId: key,
        operation: "create-static-text" as const,
        nodeId: `static.${key}`,
        semanticRole: `static-${key}`,
        valueRef: `#/profileLayout/staticContent/${key}`,
        valueHash: await sha256CanonicalJson(valueBasis),
        ...(target === "pdf"
          ? {
              valueHashAlgorithm:
                COGO_FLOW_PAGE_PDF_VALUE_HASH_ALGORITHM,
            }
          : {}),
        anchor: staticAnchor(target, key),
        ...(target === "docx" && key === "footer"
          ? { documentLink: docxFooterLink() }
          : {}),
      };
    }),
  );
  const slotOperations = await Promise.all(
    layout.slotBindings.map(async (slot, slotIndex) => {
      const valueBasis =
        target === "pdf"
          ? pdfSlotValueBasis(slot, bindings[slot.slotId])
          : bindings[slot.slotId];
      const headingBasis =
        target === "pdf"
          ? normalizeCogoFlowPagePdfText(slot.heading)
          : slot.heading;
      // requireFlowPageTableStyle is reached only on the table branch: the
      // geometry block exists exactly when the layout declares a table slot,
      // so a rich-text-only layout never touches it.
      const targetScopedStructure =
        target === "pdf"
          ? {
              target,
              structure:
                slot.slotKind === "rich-text"
                  ? ({ kind: "rich-text" } as const)
                  : ({
                      kind: "table",
                      columns: structuredClone(slot.columns),
                      columnsHash: await sha256CanonicalJson(slot.columns),
                      widthDxa: requireFlowPageTableStyle(layout, slot.slotId)
                        .widthDxa,
                      indentDxa: requireFlowPageTableStyle(layout, slot.slotId)
                        .indentDxa,
                      cellMarginsDxa: structuredClone(
                        requireFlowPageTableStyle(layout, slot.slotId)
                          .cellMarginsDxa,
                      ),
                      rowCount: (bindings[slot.slotId] as unknown[]).length,
                    } as const),
            }
          : {
              target,
              structure:
                slot.slotKind === "rich-text"
                  ? ({ kind: "rich-text" } as const)
                  : ({
                      kind: "table",
                      columns: structuredClone(slot.columns),
                      columnsHash: await sha256CanonicalJson(slot.columns),
                      widthDxa: requireFlowPageTableStyle(layout, slot.slotId)
                        .widthDxa,
                      indentDxa: requireFlowPageTableStyle(layout, slot.slotId)
                        .indentDxa,
                      rowCount: (bindings[slot.slotId] as unknown[]).length,
                    } as const),
            };
      return {
        operationId: `${target}:slot:${slot.nodeId}:${slot.slotId}`,
        bindingKind: "slot" as const,
        bindingId: slot.slotId,
        operation:
          slot.slotKind === "rich-text"
            ? ("create-rich-text" as const)
            : ("create-table" as const),
        slotId: slot.slotId,
        nodeId: slot.nodeId,
        semanticRole: slot.semanticRole,
        valueRef: `#/bindings/${slot.slotId.replaceAll("~", "~0").replaceAll("/", "~1")}`,
        valueHash: await sha256CanonicalJson(valueBasis),
        ...(target === "pdf"
          ? {
              valueHashAlgorithm:
                COGO_FLOW_PAGE_PDF_VALUE_HASH_ALGORITHM,
            }
          : {}),
        anchor:
          target === "pdf"
            ? pdfSlotAnchor(slot)
            : structuredClone(slot.targetAnchors[target]),
        heading: {
          value: slot.heading,
          valueHash: await sha256CanonicalJson(headingBasis),
          ...(target === "pdf"
            ? {
                valueHashAlgorithm:
                  COGO_FLOW_PAGE_PDF_VALUE_HASH_ALGORITHM,
              }
            : {}),
          anchor: headingAnchor(target, slot, slotIndex),
        },
        ...targetScopedStructure,
      };
    }),
  );
  const assetOperations: CogoDocumentGenerationIrV2AssetOperation[] =
    target === "pdf"
      ? []
      : resolvedAssets.map((asset, assetIndex) => ({
          operationId: `${target}:asset:${asset.assetId}`,
          target,
          bindingKind: "asset" as const,
          bindingId: asset.assetId,
          operation: "embed-asset" as const,
          assetId: asset.assetId,
          nodeId: `asset.${asset.assetId}`,
          semanticRole: asset.role,
          valueRef: `#/resolvedAssets/${assetIndex}/artifact`,
          valueHash: asset.artifact.sha256,
          anchor: assetAnchor(target, asset, assetIndex),
          placement: structuredClone(asset.placement),
          transform: {
            id: "cogo.asset-embed.identity" as const,
            version: "1.0.0" as const,
            mode: "identity" as const,
            sourceMediaType: asset.artifact.mediaType,
            outputMediaType: asset.artifact.mediaType,
            sourceSha256: asset.artifact.sha256,
            outputSha256: asset.artifact.sha256,
          },
        }));
  const operations = [...staticOperations, ...slotOperations, ...assetOperations];
  assertUniqueTargetLocators(target, operations);
  return operations;
}

async function targetPlan(
  target: CogoFlowPageNativeTarget,
  variant: Extract<CogoDocumentTemplateFamilyVariant, { layoutProfile: "flow-page" }>,
  operations: CogoDocumentGenerationIrV2Operation[],
  profileLayoutHash: string,
  resolvedAssetsHash: string,
  nativeTemplateArtifact?: CogoDocumentHashedArtifactRef,
): Promise<CogoDocumentGenerationIrV2TargetPlan> {
  const overlay = variant.targetOverlays.find((entry) => entry.target === target);
  if (
    !overlay ||
    !(overlay.target === "docx" || overlay.target === "hwpx" || overlay.target === "pdf")
  ) {
    throw new Error(`Variant ${variant.variantId} lacks a ${target} overlay.`);
  }
  if (nativeTemplateArtifact) {
    if (
      nativeTemplateArtifact.role !== "native-template" ||
      nativeTemplateArtifact.target !== target
    ) {
      throw new Error(`${target} native template artifact has the wrong role or target.`);
    }
    const errors = validateArtifactRef(
      nativeTemplateArtifact,
      `nativeTemplateArtifacts.${target}`,
    );
    if (errors.length > 0) throw new Error(errors.join(" "));
    throw new Error(
      `${target} native-template mode is blocked until source-fragment preconditions and untouched-part preservation proofs are implemented.`,
    );
  }
  if (target === "pdf") {
    if (
      overlay.target !== "pdf" ||
      overlay.outputOnly !== true ||
      overlay.editable !== false
    ) {
      throw new Error(
        "PDF generated mode requires an output-only, non-editable TemplateFamily overlay.",
      );
    }
  } else if (
    (overlay.target !== "docx" && overlay.target !== "hwpx") ||
    overlay.templateMode !== "generated-from-cogo"
  ) {
    throw new Error(
      `${target} generated mode requires a generated-from-cogo TemplateFamily overlay.`,
    );
  }
  const planWithoutHash = {
    target,
    adapter: overlay.adapter,
    sourceMode: nativeTemplateArtifact
      ? ("native-template" as const)
      : ("generated-from-cogo" as const),
    ...(nativeTemplateArtifact
      ? { nativeTemplateArtifact: structuredClone(nativeTemplateArtifact) }
      : {}),
    writer: {
      id: writerId(target),
      version: "1.0.0",
      deterministic: true as const,
      consumesExactPlan: true as const,
    },
    reader: {
      id: readerId(target),
      version: "1.0.0",
      anchorLocal: true as const,
      recomputesFromArtifact: true as const,
    },
    contentType: targetContentType(target),
    requiredPackageParts: requiredPackageParts(target),
    profileLayoutHash,
    resolvedAssetsHash,
    operations,
    readback:
      target === "pdf"
        ? {
            documentStructureRequired: true as const,
            semanticValueHashesRequired: true as const,
            nativeAnchorsRequired: true as const,
            externalConsumerOpenabilityRequired: true as const,
          }
        : {
            packageStructureRequired: true as const,
            semanticValueHashesRequired: true as const,
            nativeAnchorsRequired: true as const,
            externalConsumerOpenabilityRequired: true as const,
          },
  };
  return {
    ...planWithoutHash,
    planHash: await sha256CanonicalJson(planWithoutHash),
  };
}

export async function compileCogoFlowPageGenerationIrV2(
  input: CompileCogoFlowPageGenerationIrV2Input,
): Promise<CogoDocumentGenerationIrV2> {
  const createdAt = input.createdAt;
  const variantId = input.variantId;
  assertValidDateTime(createdAt);
  const familyArtifact = snapshotExactJsonArtifact(input.family, "family");
  const layoutArtifact = snapshotExactJsonArtifact(input.layout, "layout");
  const inputSchemaArtifact = snapshotExactJsonArtifact(
    input.inputSchema,
    "inputSchema",
  );
  const semanticInputArtifact = snapshotExactJsonArtifact(
    input.semanticInput,
    "semanticInput",
  );
  if (!Array.isArray(input.semanticEvidence)) {
    throw new Error("semanticEvidence must be an array.");
  }
  const semanticEvidenceInputs = input.semanticEvidence.map((entry, index) =>
    snapshotSemanticEvidenceInput(entry, `semanticEvidence[${index}]`),
  );
  if (!Array.isArray(input.sourceArtifacts)) {
    throw new Error("sourceArtifacts must be an array.");
  }
  const sourceArtifactInputs = input.sourceArtifacts.map((artifact, index) =>
    snapshotArtifactInput(artifact, `sourceArtifacts[${index}]`),
  );
  const nativeTemplateArtifactEntries = Object.entries(
    input.nativeTemplateArtifacts ?? {},
  ).map(([target, artifact]) => {
    if (!(target === "docx" || target === "hwpx" || target === "pdf")) {
      throw new Error(`nativeTemplateArtifacts.${target} is unsupported.`);
    }
    return [
      target,
      snapshotArtifactInput(
        artifact,
        `nativeTemplateArtifacts.${target}`,
      ),
    ] as const;
  });
  if (nativeTemplateArtifactEntries.length > 0) {
    throw new Error(
      "native-template mode is blocked until source-fragment preconditions and untouched-part preservation proofs are implemented.",
    );
  }
  if (!Array.isArray(input.requestedTargets)) {
    throw new Error(
      "requestedTargets must be a unique non-empty DOCX/HWPX/PDF list.",
    );
  }
  const requestedTargetInput = [...input.requestedTargets];

  const familyValue = familyArtifact.value;
  const layoutValue = layoutArtifact.value;

  const familyAdmission = validateDocumentTemplateFamilyAdmission(familyValue);
  if (!familyAdmission.admitted) {
    throw new Error(
      `Invalid TemplateFamily: ${[
        ...familyAdmission.schema.issues,
        ...familyAdmission.semantic.issues,
      ].join(" ")}`,
    );
  }
  const family = structuredClone(familyValue) as CogoDocumentTemplateFamily;
  // Resolved before FlowPage validation so the layout validator can gate
  // its HWPX anchor requirement on this variant's declared renderTargets
  // instead of demanding an anchor unconditionally.
  const variant = flowVariant(family, variantId);
  const layoutErrors = validateDocumentTemplateFlowPage(
    layoutValue,
    variant.renderTargets,
  );
  if (layoutErrors.length > 0) {
    throw new Error(`Invalid FlowPage layout: ${layoutErrors.join(" ")}`);
  }
  const layout = structuredClone(layoutValue) as CogoDocumentTemplateFlowPage;
  // validateDocumentTemplateFlowPage above admits a missing assetPlacements
  // key as an implicit empty array; materialize that default here so every
  // downstream reader (resolveFamilyResourceArtifacts iterates it
  // unconditionally) sees a real array rather than undefined. This mutates
  // the post-clone `layout`, not `layoutArtifact`/`layoutValue` (already
  // snapshotted independently above), so it cannot affect layoutLineage's
  // hash of the original bytes.
  if (layout.assetPlacements === undefined) {
    layout.assetPlacements = [];
  }
  const familyLineage = await jsonLineage(familyArtifact);
  const layoutLineage = await jsonLineage(layoutArtifact);
  const inputSchemaLineage = await jsonLineage(inputSchemaArtifact);
  const semanticInputLineage = await jsonLineage(semanticInputArtifact);
  const jsonResourceRefs = [
    familyLineage.ref,
    layoutLineage.ref,
    inputSchemaLineage.ref,
    semanticInputLineage.ref,
  ];
  if (!unique(jsonResourceRefs)) {
    throw new Error("Family, layout, input schema, and semantic input refs must be unique.");
  }
  validateFamilyLayoutContract(family, variant, layout, {
    familyRef: familyLineage.ref,
    layoutRef: layoutLineage.ref,
    layoutByteSha256: layoutLineage.byteSha256,
    inputSchemaRef: inputSchemaLineage.ref,
    inputSchemaByteSha256: inputSchemaLineage.byteSha256,
  });

  const inputErrors = validateDocumentTemplateInput(
    inputSchemaArtifact.value,
    semanticInputArtifact.value,
  );
  if (inputErrors.length > 0) {
    throw new Error(`Invalid semantic input: ${inputErrors.join(" ")}`);
  }
  if (
    requestedTargetInput.length === 0 ||
    !unique(requestedTargetInput) ||
    requestedTargetInput.some(
      (target) =>
        !(target === "docx" || target === "hwpx" || target === "pdf"),
    )
  ) {
    throw new Error(
      "requestedTargets must be a unique non-empty DOCX/HWPX/PDF list.",
    );
  }
  for (const target of requestedTargetInput) {
    if (!variant.renderTargets.includes(target)) {
      throw new Error(`Variant ${variant.variantId} does not declare target ${target}.`);
    }
  }
  const requestedTargets = [...requestedTargetInput].sort(
    (left, right) =>
      COGO_FLOW_PAGE_TARGET_ORDER.indexOf(left) -
      COGO_FLOW_PAGE_TARGET_ORDER.indexOf(right),
  );
  if (sourceArtifactInputs.some((artifact) => artifact.role === "native-template")) {
    throw new Error(
      "Native templates must use nativeTemplateArtifacts and remain fail-closed.",
    );
  }
  const sourceRefs = sourceArtifactInputs.map((artifact) => artifact.ref);
  if (!unique(sourceRefs)) throw new Error("sourceArtifacts refs must be unique.");
  if (!unique([...jsonResourceRefs, ...sourceRefs])) {
    throw new Error(
      "JSON resources and sourceArtifacts must use globally unique refs.",
    );
  }
  const sourceArtifacts = (
    await Promise.all(sourceArtifactInputs.map(hashArtifactInput))
  ).sort((left, right) => left.ref.localeCompare(right.ref));
  const semanticProvenance = await buildSemanticProvenance(
    semanticInputArtifact.value,
    semanticEvidenceInputs,
    sourceArtifactInputs,
    sourceArtifacts,
  );
  const expectedSourceRefs = [
    ...semanticProvenance.evidence.map((entry) => entry.sourceRef),
    ...family.legacyTemplateRefs.map((entry) =>
      resolveDeclaredRef(familyLineage.ref, entry.manifestRef),
    ),
    ...family.semanticCore.assets.map((entry) =>
      resolveDeclaredRef(familyLineage.ref, entry.sourceRef),
    ),
  ];
  if (
    canonicalizeDocumentTemplateJson([...new Set(expectedSourceRefs)].sort()) !==
    canonicalizeDocumentTemplateJson(
      sourceArtifacts.map((entry) => entry.ref).sort(),
    )
  ) {
    throw new Error(
      "sourceArtifacts must exactly equal semantic evidence, legacy manifest, and TemplateFamily asset refs.",
    );
  }
  const nativeTemplateArtifacts: Partial<
    Record<CogoFlowPageNativeTarget, CogoDocumentHashedArtifactRef>
  > = {};
  const resolvedAssets = resolveFamilyResourceArtifacts(
    family,
    familyLineage.ref,
    layout,
    layoutLineage,
    inputSchemaLineage,
    sourceArtifacts,
    sourceArtifactInputs,
  );

  const bindings = bindFlowPageSlots(layout, semanticInputArtifact.value);
  const variantHash = await sha256CanonicalJson(variant);
  const profileLayoutHash = await sha256CanonicalJson(layout);
  const resolvedAssetsHash = await sha256CanonicalJson(resolvedAssets);

  const writePlan = (
    await Promise.all(
      requestedTargets.map((target) =>
        buildOperations(target, layout, bindings, resolvedAssets),
      ),
    )
  ).flat();
  const targetPlans = await Promise.all(
    requestedTargets.map((target) =>
      targetPlan(
        target,
        variant,
        writePlan.filter((operation) => operation.target === target),
        profileLayoutHash,
        resolvedAssetsHash,
        nativeTemplateArtifacts[target],
      ),
    ),
  );

  const structureBasis = {
    schemaVersion: COGO_DOCUMENT_GENERATION_IR_V2_SCHEMA_VERSION,
    compiler: COGO_DOCUMENT_GENERATION_IR_V2_COMPILER,
    familyCanonicalSha256: familyLineage.canonicalSha256,
    variantCanonicalSha256: variantHash,
    layoutCanonicalSha256: layoutLineage.canonicalSha256,
    inputSchemaCanonicalSha256: inputSchemaLineage.canonicalSha256,
    requestedTargets,
    profileLayout: layout,
    resolvedAssets,
    writePlan: writePlan.map(({ valueHash: _valueHash, ...operation }) => operation),
    targetPlans: targetPlans.map(({ planHash: _planHash, ...plan }) => ({
      ...plan,
      operations: plan.operations.map(({ valueHash: _valueHash, ...operation }) =>
        operation,
      ),
    })),
    unsupportedNativePartPolicy: layout.unsupportedNativePartPolicy,
  };
  const contentBasis = {
    semanticInputCanonicalSha256: semanticInputLineage.canonicalSha256,
    semanticProvenance,
    bindings,
    sourceArtifacts,
    resolvedAssets,
    nativeTemplateArtifacts,
  };
  const structureHash = await sha256CanonicalJson(structureBasis);
  const contentHash = await sha256CanonicalJson(contentBasis);
  const proofBindingBasis = {
    compiler: COGO_DOCUMENT_GENERATION_IR_V2_COMPILER,
    family: familyLineage,
    variant: { variantId: variant.variantId, canonicalSha256: variantHash },
    layout: layoutLineage,
    inputSchema: inputSchemaLineage,
    semanticInput: semanticInputLineage,
    semanticProvenance,
    sourceArtifacts,
    resolvedAssets,
    structureHash,
    contentHash,
    targetPlans,
  };
  const proofBindingHash = await sha256CanonicalJson(proofBindingBasis);

  return {
    schemaVersion: COGO_DOCUMENT_GENERATION_IR_V2_SCHEMA_VERSION,
    generationId: `${family.familyId}.${family.familyVersion}.${variant.variantId}.${proofBindingHash.slice(-16)}`,
    createdAt,
    profile: "flow-page",
    compiler: COGO_DOCUMENT_GENERATION_IR_V2_COMPILER,
    family: {
      ...familyLineage,
      familyId: family.familyId,
      familyVersion: family.familyVersion,
    },
    variant: {
      variantId: variant.variantId,
      profile: "flow-page",
      canonicalSha256: variantHash,
    },
    layout: { ...layoutLineage, layoutId: layout.layoutId },
    inputSchema: inputSchemaLineage,
    semanticInput: semanticInputLineage,
    semanticProvenance,
    sourceArtifacts,
    profileLayout: structuredClone(layout),
    resolvedAssets,
    bindings,
    writePlan,
    requestedTargets,
    targetPlans,
    unsupportedNativePartPolicy: layout.unsupportedNativePartPolicy,
    structureHash,
    contentHash,
    proofBindingHash,
  };
}

export async function verifyCogoFlowPageGenerationIrV2(
  ir: CogoDocumentGenerationIrV2,
  resources: VerifyCogoFlowPageGenerationIrV2Resources,
): Promise<string[]> {
  const issues: string[] = [];
  let candidate: CogoDocumentGenerationIrV2;
  try {
    candidate = structuredClone(ir);
  } catch {
    return ["IR candidate must be synchronously snapshot-compatible."];
  }
  let expected: CogoDocumentGenerationIrV2;
  try {
    const { request, ...artifacts } = resources;
    expected = await compileCogoFlowPageGenerationIrV2({
      ...artifacts,
      variantId: request.variantId,
      requestedTargets: request.requestedTargets,
      createdAt: request.createdAt,
    });
  } catch (error) {
    return [error instanceof Error ? error.message : String(error)];
  }
  if (
    canonicalizeDocumentTemplateJson(Object.keys(candidate).sort()) !==
    canonicalizeDocumentTemplateJson(Object.keys(expected).sort())
  ) {
    issues.push("IR top-level fields do not exactly match the recomputed contract.");
  }
  const comparisons: Array<[string, unknown, unknown]> = [
    ["schemaVersion", candidate.schemaVersion, expected.schemaVersion],
    ["generationId", candidate.generationId, expected.generationId],
    ["createdAt", candidate.createdAt, expected.createdAt],
    ["profile", candidate.profile, expected.profile],
    ["compiler", candidate.compiler, expected.compiler],
    ["family", candidate.family, expected.family],
    ["variant", candidate.variant, expected.variant],
    ["layout", candidate.layout, expected.layout],
    ["inputSchema", candidate.inputSchema, expected.inputSchema],
    ["semanticInput", candidate.semanticInput, expected.semanticInput],
    [
      "semanticProvenance",
      candidate.semanticProvenance,
      expected.semanticProvenance,
    ],
    ["sourceArtifacts", candidate.sourceArtifacts, expected.sourceArtifacts],
    ["profileLayout", candidate.profileLayout, expected.profileLayout],
    ["resolvedAssets", candidate.resolvedAssets, expected.resolvedAssets],
    ["bindings", candidate.bindings, expected.bindings],
    ["writePlan", candidate.writePlan, expected.writePlan],
    [
      "requestedTargets",
      candidate.requestedTargets,
      expected.requestedTargets,
    ],
    ["targetPlans", candidate.targetPlans, expected.targetPlans],
    [
      "unsupportedNativePartPolicy",
      candidate.unsupportedNativePartPolicy,
      expected.unsupportedNativePartPolicy,
    ],
    ["structureHash", candidate.structureHash, expected.structureHash],
    ["contentHash", candidate.contentHash, expected.contentHash],
    ["proofBindingHash", candidate.proofBindingHash, expected.proofBindingHash],
  ];
  for (const [label, actual, recomputed] of comparisons) {
    if (
      canonicalizeDocumentTemplateJson(actual) !==
      canonicalizeDocumentTemplateJson(recomputed)
    ) {
      issues.push(`${label} does not match independently recomputed IR.`);
    }
  }
  return issues;
}
